/* ============================================
   QUOTE DASHBOARD — Shipping / Freight Cards (Bucket Panel + Freight Balance + Pallet Form)
   Asset: dashboard-shipping-cards.js

   Extracted from dashboard-main.js on 2026-05-22 as Phase B.1.a of the
   dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js so the RQD bindings
   exist by the time main runs.

   Public API (attached to window.RQD):
     renderShippingBucketPanel(bucket, q, ref, dimsEntered, dimsRaw, dimsSummary, vendor)
     renderFreightBalanceCard(q)
     renderFreightBalancePalletRow(p, isFirst)
     renderFreightBalancePalletForm(ref, existing, vendor)

   Init contract (RQD.initShippingCards called once by main.js at boot):
     esc, money, formatDate, sectionTitleHtml,
     isFreightBalanced, isPostShipInvoiceVendor,
     getIsAdmin (closure-getter — isAdmin mutates after auth resolves)

   sectionTitleHtml currently sourced from main.js. After Phase B.1.b
   ships dashboard-order-cards.js, the init call may switch to passing
   window.RQD.sectionTitleHtml directly.

   Internal cross-references between extracted functions use BARE names
   (sibling functions in this IIFE — same scope as before extraction).
   Only deps from main.js's IIFE go through the init-deps surface.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initShippingCards (from main.js boot)
  var esc, money, formatDate, sectionTitleHtml;
  var isFreightBalanced, isPostShipInvoiceVendor;
  var getIsAdmin;

  /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-29) — "is this line freight-quoted?", ONCE, for
     every client surface that asks.

     ⛔ THREE SOURCES, AND THE CLIENT KNEW TWO. The auto freight-quote weight rule (a vendor
     shipment at or above the threshold pallets itself) shipped 2026-08-28 into the worker's
     bucket derivation. Every consumer that kept its own tag-or-flag copy then disagreed with
     the board on the same screen — worker-side that cost SHOP-1996 its PO entirely
     (rfq-worker `81e42e3`); client-side it greys out the Split button on an order that IS
     freight, and can offer "Request Freight" under a pill reading freight-quoted.

     ⛔ THE ANSWER IS ALREADY IN THE PAYLOAD. `q._autoFreightByVendor` arrives on every row with
     the per-vendor arithmetic the Order Contents pill renders, so this needs no endpoint and no
     new fetch — it needs the two places that ask to stop guessing.

     ⚠️ `it.vendor` IS ALREADY CANONICAL. The worker runs `canonicalVendor` when it builds
     `onlineLineItems`, and keys `_autoFreightByVendor` the same way — so the line says "ECORE"
     where Shopify says "ECORE Athletic", and a raw-vendor lookup here would silently miss.

     ⚠️ `tripped` ALREADY CARRIES THE OVERRIDE. An explicit off on any of a vendor's lines makes
     the worker return `tripped: false, overridden: true`, so this must not re-test it per line —
     that would re-apply a vendor-wide override as a line-wide one and disagree again. */
  function lineIsFreightQuoted(it, q) {
    if (!it) return false;
    if (it.freightQuotedFlagOff) return false;
    if ((it.productTags || []).some(function (t) { return String(t).toLowerCase() === 'freight-quoted'; })) return true;
    if (it.isFreightQuotedFlag) return true;
    var auto = (q && q._autoFreightByVendor) || null;
    var entry = auto ? auto[it.vendor || ''] : null;
    return !!(entry && entry.tripped);
  }
  window.RQD.lineIsFreightQuoted = lineIsFreightQuoted;

  /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-28) — the date beside a "requested" row, which now
     has two meanings. The ⏱ box holds a freight / weight-dims request until the next 6am–6pm MT
     window, and Ryan's call was that the row still reads REQUESTED the moment Send is clicked, so
     nobody asks the same supplier twice while it sits in the queue. That makes the stamp
     ambiguous on its own — "requested 8/28" on an email nobody has received yet — so a held
     request says so and names the hour it goes.

     ⚠️ `scheduledFor` in the PAST is not a held request: the drain sends and clears it, but a
     record read mid-tick (or a drain that failed and is retrying) would otherwise keep claiming
     a future send forever. Past → fall back to the plain stamp, which is the honest answer. */
  function requestedStampHtml(entry) {
    entry = entry || {};
    var stamp = entry.requestedAt ? formatDate(entry.requestedAt) : '';
    var sched = entry.scheduledFor ? new Date(entry.scheduledFor) : null;
    if (!sched || isNaN(sched.getTime()) || sched.getTime() <= Date.now()) return stamp;
    var when = sched.toLocaleString('en-US', {
      timeZone: 'America/Denver', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
    return '<span title="Held for business hours — this email sends automatically at '
      + esc(when) + ' MT.">' + esc(stamp) + ' · ⏱ sends ' + esc(when) + '</span>';
  }
  /* END CUSTOM_SENDWINDOW_BY_CLAUDE */

  /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) — the "⏳ Scheduled" pill, now a release control,
     rendered from ONE place for all four rows that can hold a vendor email.

     Ryan asked first for a "release now" link beside the freight row's `cancel`, then corrected
     himself on seeing SHOP-1996: *"SHOP-1996 has a green 'scheduled' button. Should be able to
     click that and have the option to release it actually. SHOP-1999 has no such button, and it
     should — adding that ... means we don't need the 'release now' text/button."* So the fix is
     one affordance in four places, not a second affordance beside the first.

     ⛔ THE PILL USED TO BE DELIBERATELY INERT, and the reason no longer holds. Its CSS comment
     says clicking it "would queue a SECOND copy of the same email" — true while the only thing a
     click could mean was re-firing Email PO. A click now means RELEASE, which is the opposite: it
     drains the copy already queued. The amber-ghost look is unchanged, because `scheduled` still
     is not `sent` and the pill must not read as done.

     ⚠️ A PAST `scheduledFor` RENDERS NOTHING, matching requestedStampHtml above. The drain clears
     the marker after it sends, so a past time means either mid-tick or a failed retry — offering
     Release on it would invite a second send of something that may already have gone.

     ⚠️ ADMIN ONLY. The endpoint enforces it; this hides the control so a rep is not offered a
     button that will 403. */
  function scheduledReleaseBtnHtml(o) {
    o = o || {};
    var sched = o.scheduledFor ? new Date(o.scheduledFor) : null;
    if (!sched || isNaN(sched.getTime()) || sched.getTime() <= Date.now()) return '';
    if (typeof getIsAdmin === 'function' && !getIsAdmin()) return '';
    var when = sched.toLocaleString('en-US', {
      timeZone: 'America/Denver', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
    var attrs = ' data-release-email="' + esc(o.kind) + '" data-ref="' + esc(o.ref || '')
      + '" data-when="' + esc(when) + '"';
    /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) — WHAT this email is, in words, for the
       accessible name below. */
    var subject;
    if (o.kind === 'po') {
      attrs += ' data-po="' + esc(o.poNumber || '') + '"';
      subject = 'PO ' + (o.poNumber || '');
    } else {
      attrs += ' data-vendor="' + esc(o.vendor || '') + '" data-type="' + esc(o.type || '')
        + '" data-bucket="' + esc(o.bucket || '') + '"';
      subject = (o.type === 'dims' ? 'weight & dims' : 'freight quote') + ' request to '
        + (o.vendor || 'the supplier');
    }
    /* ⛔ THE ACTION LIVES IN THE ACCESSIBLE NAME, NOT ONLY IN `title`. Ryan's standing constraint:
       *"we need this tool to be usable by AI agents that can't read tooltips."* A `title` is a
       hover affordance and nothing else reads it — an agent driving this page by accessible name
       would see a button called "Scheduled" and have no way to know it can be released.
       `aria-label` IS the accessible name, sits in the DOM, and overrides the visible text for
       anything reading the tree. The visible label stays "⏳ Scheduled" because that is the row's
       STATUS, which is the other half of what has to be legible. */
    var label = 'Scheduled — click to send the ' + subject + ' email now instead of at '
      + when + ' MT';
    var html = '<button class="rqd-invoice__link rqd-po-scheduled rqd-po-scheduled--releasable" type="button"'
      + attrs + ' aria-label="' + esc(label) + '" title="' + esc(label)
      + '">&#9203; Scheduled</button>';
    /* CUSTOM_FREIGHTABORT_BY_CLAUDE (AIOPS e653a9fd28de, Ryan 2026-09-04) — the other way out of
       a hold. Release sends it now; this un-sends it. Freight / dims asks only: a held PO has its
       own record and its own undo (void + re-generate). Same data attributes, its own hook, and
       the action in the accessible name for the same reason as the pill above. */
    if (o.kind !== 'po') {
      var cancelLabel = 'Cancel the held ' + subject + ' email so it never sends (due ' + when + ' MT)';
      html += ' <button class="rqd-freight-reset-link rqd-po-scheduled--cancel" type="button"'
        + attrs.replace('data-release-email=', 'data-cancel-email=')
        + ' aria-label="' + esc(cancelLabel) + '" title="' + esc(cancelLabel) + '">cancel</button>';
    }
    return html;
  }
  window.RQD.scheduledReleaseBtnHtml = scheduledReleaseBtnHtml;
  /* END CUSTOM_RELEASENOW_BY_CLAUDE */

  /* CUSTOM_LEGENDSPEC_BY_CLAUDE (Ryan 2026-08-13) — short vendor name for the fixed-width buttons
     on the Fulfillment card.

     .rqd-invoice__link is a fixed 140px x 28px box with `white-space: nowrap` and NO overflow rule,
     so a long label neither wraps nor clips — it simply spills out past the border. That is what
     "Email Legend Fitness" was doing. Ryan: *"just change it to 'Email Legend'"*.

     Only a TRAILING ornamental word is dropped, leaving the name people actually say out loud:
     Legend Fitness -> Legend, ECORE Athletic -> ECORE, TKO Strength -> TKO. Never applied to a
     single-word vendor (Hyperice stays Hyperice), and never when it would leave under three
     characters. Nothing is lost from the CARD — the row label beside the button still reads
     "Request from Legend Fitness" in full, and the button carries the full name as its title.

     A CSS ellipsis backstop rides in dashboard.css for any vendor this doesn't shorten enough. */
  var VENDOR_TAIL_WORDS = ['fitness', 'athletic', 'athletics', 'strength', 'industries',
    'systems', 'designs', 'international', 'equipment', 'company', 'inc', 'llc', 'co', 'corp'];

  /* ~12 characters is what clears the 140px button at 12px once "Email " is in front of it. */
  var VENDOR_LABEL_MAX = 12;

  function shortVendorLabel(name) {
    var full = String(name || '').trim();
    var words = full.split(/\s+/);
    if (words.length < 2) return full;

    // 1. Drop a trailing ornamental word: "Legend Fitness" -> "Legend".
    var last = words[words.length - 1].toLowerCase().replace(/[^a-z]/g, '');
    if (VENDOR_TAIL_WORDS.indexOf(last) !== -1 && words.slice(0, -1).join(' ').length >= 3) {
      words = words.slice(0, -1);
    }

    /* 2. Still too long for the button — fall back to the first word alone. This is what stops
          "Cascade Health and Fitness" becoming "Cascade Health and", left dangling on a connector
          by step 1, and it shortens names with no ornamental tail at all ("Perform Better" ->
          "Perform"). Handling both with a length rule beats maintaining a list of connectors. */
    if (words.join(' ').length > VENDOR_LABEL_MAX && words.length > 1 && words[0].length >= 3) {
      words = [words[0]];
    }

    var out = words.join(' ');
    return out.length >= 3 ? out : full;
  }

    // === renderFreightBalancePalletRow — extracted from main.js:6588-6607 ===
    // ── Freight Balance: multi-row pallet entry form (row 2 of the card) ──
    // Each row = one pallet group (Qty × L × W × H + Weight per pallet).
    // Multiple rows when a single shipment has differently-spec'd pallets.
    // Save button right-aligned to the card's 12 px right inset so it
    // lines up vertically with the Re-send / Generate Invoice / Generate PO
    // / Send to Customer buttons across the rest of the panel.
    function renderFreightBalancePalletRow(p, isFirst) {
      var qty = (p && p.qty) || 1;
      var l = (p && p.length != null) ? p.length : '';
      var w = (p && p.width != null) ? p.width : '';
      var h = (p && p.height != null) ? p.height : '';
      var wt = (p && p.weight != null) ? p.weight : '';
      return '<div class="rqd-fb-pallet-row">' +
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — Remove ✕ moved to the
           LEFT of the inputs for added (2nd+) rows. The row is flex-end so the
           inputs still hug the right edge / align with the first row; the ✕ just
           hangs in the left gutter instead of pushing the inputs left. */
        (isFirst
          ? '<span class="rqd-fb-pallet-remove rqd-fb-pallet-remove--placeholder"></span>'
          : '<button type="button" class="rqd-fb-pallet-remove" data-fb-action="remove-pallet-row" title="Remove this row">×</button>') +
        '<input type="number" class="rqd-fb-pallet-qty" min="1" step="1" placeholder="1" value="' + esc(qty) + '" title="How many pallets in this group">' +
        '<span class="rqd-fb-pallet-sep">×</span>' +
        '<input type="number" class="rqd-fb-pallet-l" min="0" step="0.01" placeholder="L" value="' + esc(l) + '">' +
        '<span class="rqd-fb-pallet-sep">×</span>' +
        '<input type="number" class="rqd-fb-pallet-w" min="0" step="0.01" placeholder="W" value="' + esc(w) + '">' +
        '<span class="rqd-fb-pallet-sep">×</span>' +
        '<input type="number" class="rqd-fb-pallet-h" min="0" step="0.01" placeholder="H" value="' + esc(h) + '">' +
        /* CUSTOM_DIMSFORM_BY_CLAUDE (Ryan 2026-08-27) - "lbs", not "Weight". The field sits last
        in an L x W x H row where every other box is already a dimension, so the open question
        is the UNIT, not what the box is for. */
        '<input type="number" class="rqd-fb-pallet-weight" min="0" step="0.01" placeholder="lbs" value="' + esc(wt) + '">' +
        '</div>';
    }

    // === renderFreightBalancePalletForm — extracted from main.js:6608-6648 ===
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — "Weight/dims received"
       rendered as N/A. Used when the supplier shipped on their own carrier and
       no dims will ever come back. Shows a ✓ + an explicit "N/A — supplier
       shipped" label (never confused with captured dims) + an Undo that reopens
       entry. Satisfies the same fulfillment gate as real dims. */
    function renderDimsNaDoneRow(ref, dimsObj) {
      var reason = (dimsObj && dimsObj.reason) ? String(dimsObj.reason) : '';
      var meta = 'N/A — supplier shipped' + (reason ? ' · ' + reason : '');
      return '<div class="rqd-toggle rqd-toggle--done" data-ref="' + esc(ref) + '">' +
        '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>' +
        '<span>Weight/dims received</span>' +
        '<span class="rqd-toggle__date" title="Marked N/A — no weight/dims will be received for this shipment">' + esc(meta) + '</span>' +
        '<div class="rqd-invoice" style="margin-left:auto;">' +
          /* Done-state treatment — the muted green ghost-outline variant
             (.rqd-invoice__generate, NOT --active), matching the Re-send /
             Re-gen / Re-send PO buttons: reads as "done, still available",
             not a prominent call-to-action. */
          '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="dims-na-clear" data-ref="' + esc(ref) + '" title="Undo N/A — reopen weight/dims entry">Undo</button>' +
        '</div>' +
      '</div>';
    }

    function renderFreightBalancePalletForm(ref, existing, vendor) {
      var pallets = (Array.isArray(existing) && existing.length > 0)
        ? existing
        : [{ qty: 1 }];
      var rowsHtml = pallets.map(function (p, i) {
        return renderFreightBalancePalletRow(p, i === 0);
      }).join('');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Vendor parameter is
         optional. When provided, the form's wrapper carries data-vendor so
         the save handler routes the dims to lifecycle.ordering[vendor]
         .freightDims (per-vendor key). When absent, save falls back to the
         legacy global lifecycle.freightBalance.dims path. Remove button
         renders when editing (existing pallets present) so admin can wipe
         this vendor's dims back to "no dims" state. */
      var vendorAttr = vendor ? ' data-vendor="' + esc(vendor) + '"' : '';
      var hasExisting = Array.isArray(existing) && existing.length > 0;
      return '<div class="rqd-fb-pallet-form" data-ref="' + esc(ref) + '"' + vendorAttr + '>' +
        '<div class="rqd-toggle" data-ref="' + esc(ref) + '">' +
          '<div class="rqd-toggle__check" style="cursor:default;"></div>' +
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Vendor name
             dropped from the label. With the form now visually indented
             as a child of the parent vendor row (.rqd-vendor-dims-block
             .rqd-toggle padding-left:28px), redundantly repeating
             "— {VENDOR}" reads as noise. The data-vendor attr on the
             form wrapper still carries the context for the save handler. */
          '<span>Weight/dims received</span>' +
          '<div class="rqd-fb-pallet-rows">' + rowsHtml + '</div>' +
        '</div>' +
        '<div class="rqd-fb-pallet-actions">' +
          '<button type="button" class="rqd-fb-btn rqd-fb-pallet-add" data-fb-action="add-pallet-row" data-ref="' + esc(ref) + '">+ Pallet</button>' +
          (hasExisting && vendor ? '<button type="button" class="rqd-fb-btn rqd-fb-pallet-remove-all" data-fb-action="remove-vendor-dims" data-ref="' + esc(ref) + '" data-vendor="' + esc(vendor) + '" style="color:#b91c1c;">Remove</button>' : '') +
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — N/A escape hatch for
             outlier shipments the supplier sent on their own carrier (no dims
             coming). Marks the step done so the order can close; reversible. */
          '<button type="button" class="rqd-fb-btn rqd-fb-pallet-na" data-fb-action="dims-na" data-ref="' + esc(ref) + '"' + vendorAttr + ' title="No weight/dims will be received (supplier shipped on their own carrier). Marks this step complete so the order can close out. Reversible.">N/A</button>' +
          /* Cancel reverts the form state without saving. Handler just
             fetchQuotes() so the re-render reflects what's actually in KV
             (empty → "+ Log dims" button reappears; saved → saved-dims
             chip reappears). Lets admin opt out without committing partial
             data. */
          '<button type="button" class="rqd-fb-btn rqd-fb-pallet-cancel" data-fb-action="cancel-pallet-entry">Cancel</button>' +
          '<button type="button" class="rqd-invoice__link rqd-fb-btn--primary rqd-fb-pallet-save" data-fb-action="save-pallets" data-ref="' + esc(ref) + '"' + vendorAttr + '>Save</button>' +
        '</div>' +
        '</div>';
    }

    // === renderShippingBucketPanel — extracted from main.js:6672-6936 ===
    // ── Freight Balance card (Flow A only) ──
    // 4 rows that progress sequentially. Each row is .rqd-toggle (matches
    // AR/AP + Shipping vocabulary). Inputs/buttons collapse once the step
    // is complete; the row's checkbox flips to ✓ and a date stamps in.
    //
    //   Row 1: Weight/Dims Requested  → button to email supplier
    //   Row 2: Weight/Dims Received   → text input + Save
    //   Row 3: Broker Quote + Invoice → number input + Generate Invoice
    //                                   (worker mints FREIGHT-{ref} draft +
    //                                    Claude-drafts customer email)
    //   Row 4: Balance Paid           → "Mark Paid" fallback button
    //                                   (auto-flips on orders/paid webhook)
    //
    // CUSTOM_RFQPORTAL_BY_CLAUDE — Shipping bucket panel renderer.
    // Each bucket is a self-contained sub-workflow:
    //   Broker bucket: Request weight/dims → Receive dims (pallet inputs)
    //                  → Freight cost → PO
    //   Parcel bucket: Request freight quote → Freight cost → PO
    // The two share the supplier email modal (window.DashboardHandshake)
    // but the bucket key threads through so worker stores per-bucket
    // request state (workstation_meta.shippingBuckets[id].request) and
    // the email lists only this bucket's line items.
    /* CUSTOM_ALTSHIP_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - a bucket shared by a brand and its alternate source:
       one block per supplier ("ECORE - Lancaster, PA" / "Recycled Surfacing - Lexington, KY"), each with its own
       request, Log dims, Freight cost and Purchase order rows, then ONE Customer balance row for the order whose
       "will charge" is the sum of the shipments' freight costs. Exposed for the tests. */
    function sharedBucketView(b, s, extra) {
      var view = {};
      for (var k in b) { if (Object.prototype.hasOwnProperty.call(b, k) && k !== 'suppliers') view[k] = b[k]; }
      ['lineItemIds', 'lineItemCount', 'freightCost', 'freightCostSet', 'requestKind', 'requestKindOverridden',
        'requiresCostBeforePo', 'requestedAt', 'requestedBy', 'scheduledFor', 'priorRequest'].forEach(function (k) {
        if (s && Object.prototype.hasOwnProperty.call(s, k)) view[k] = s[k];
      });
      if (s && !s.isBrand) view.parcelRatePending = null;
      for (var e in (extra || {})) view[e] = extra[e];
      return view;
    }
    function renderSharedBucket(b, q, ref, dimsEntered, dimsRaw, dimsSummary, vendor) {
      var sups = b.suppliers;
      var total = 0, allSaved = true;
      sups.forEach(function (s) {
        total += parseFloat(s && s.freightCost) || 0;
        if (!(s && (s.freightCostSet || parseFloat(s.freightCost) > 0))) allSaved = false;
      });
      total = Math.round(total * 100) / 100;
      var shared = { total: total, allSaved: allSaved, count: sups.length };
      var out = '<div class="rqd-fb__shared" data-bucket="' + esc(b.id) + '">';
      if (Array.isArray(q.shippingBuckets) && q.shippingBuckets.length > 1) {
        out += '<div class="rqd-fb__shared-label">' + esc(b.label || b.id) + '</div>';
      }
      sups.forEach(function (s) {
        if (!s) return;
        var d = s.isBrand ? null : (s.dims || null);
        var dObj = d && typeof d === 'object' ? d : null;
        var dRaw = s.isBrand ? dimsRaw : (dObj ? (dObj.raw || '') : (d || ''));
        var dSum = s.isBrand ? dimsSummary : ((dObj && dObj.summary) || '');
        var dEnt = s.isBrand ? dimsEntered : !!(dRaw && String(dRaw).trim().length);
        out += '<div class="rqd-po-group rqd-fb__supplier-head" data-supplier-head="' + esc(s.vendor) + '">'
          + esc(s.vendor) + '</div>'; // CUSTOM_ALTSHIP_BY_CLAUDE (Ryan 2026-10-09) - the name only, no ship-from city
        out += renderShippingBucketPanel(sharedBucketView(b, s, { _supplier: s, _shared: shared }), q, ref, dEnt, dRaw, dSum, s.vendor);
      });
      // The order's one Customer balance row (and roll lengths), under the shipments.
      out += renderShippingBucketPanel(sharedBucketView(b, null, {
        // Ready only once EVERY shipment has its cost; until then the row reads as waiting on the costs above.
        _shared: shared, _balanceOnly: true, freightCost: allSaved ? total : 0, freightCostSet: allSaved && total > 0,
      }), q, ref, dimsEntered, dimsRaw, dimsSummary, vendor);
      return out + '</div>';
    }

    function renderShippingBucketPanel(bucket, q, ref, dimsEntered, dimsRaw, dimsSummary, vendor) {
      /* CUSTOM_BALANCEBTN_BY_CLAUDE (Ryan 2026-08-28) — ⛔ BARE `isAdmin` DOES NOT EXIST HERE.
         The "No Balance" button moved into this function referencing it, and on the first deal
         that actually rendered a bucket panel (SHOP-1996, the moment the weight rule promoted it
         to Flow A) that threw a ReferenceError, took the whole detail render down with it, and the
         row simply stopped expanding — no error surfaced to the user, just a dead click.

         `isAdmin` is a local of renderFreightBalanceCard, resolved there the same way. It has to
         be READ THROUGH THE GETTER rather than captured at module load: admin state mutates after
         auth resolves, which is why the module holds `getIsAdmin` and not a boolean. */
      var isAdmin = getIsAdmin ? getIsAdmin() : false;
      var bId = bucket.id;
      /* CUSTOM_ALTSHIP_BY_CLAUDE (Ryan 2026-10-09, SHOP-2128) - ONE SHIPMENT PER SUPPLIER. When a bucket is shared
         by a brand and its alternate source, renderSharedBucket calls this once per supplier with a VIEW of the
         bucket carrying that supplier's own fields (`_supplier`), then once more with `_balanceOnly` for the one
         Customer balance row the order has. `data-supplier` marks every control that writes an ALTERNATE's own
         slot (cost, request kind, dims); the brand's controls carry none and write the bucket as they always did.
         ⛔ With no `_supplier` on the bucket nothing below changes. */
      var _sp = bucket._supplier || null;
      var _spAlt = !!(_sp && !_sp.isBrand);
      var _shared = bucket._shared || null;
      var _balOnly = !!bucket._balanceOnly;
      var _spAttr = _spAlt ? ' data-supplier="' + esc(_sp.vendor) + '"' : '';
      var bLabel = esc(bucket.label || bId);
      // CUSTOM_ABBY_BY_CLAUDE (2026-06-02) — empty (not "0.00") when no cost is
      // saved, so the placeholder shows and the first keystroke overwrites
      // cleanly instead of appending to a literal "0.00" (typing 99 → "990.00").
      var costStr = (!!bucket.freightCostSet || parseFloat(bucket.freightCost) > 0)
        ? (parseFloat(bucket.freightCost) || 0).toFixed(2)
        : '';
      // costSaved = admin has explicitly entered a freight cost (zero or
      // positive). bucket.freightCostSet is true whenever
      // wm.shippingBuckets[*].freightCost is defined — accepts $0 as
      // valid (admin closing out a parcel-rate bucket where the vendor
      // didn't bill). Falls back to legacy `> 0` for older payloads.
      var costSaved = !!bucket.freightCostSet || parseFloat(bucket.freightCost) > 0;
      // parcelRatePending = set at PO generation when the PO body printed
      // "Per supplier's standard parcel rates" (vendor shippingModel =
      // parcel-only, no freight cost). Drives the ⚠ banner below the
      // Freight cost row prompting admin to enter the actual cost when
      // the supplier's invoice arrives.
      var parcelRatePending = !_spAlt && !costSaved && bucket.parcelRatePending && bucket.parcelRatePending.since; // CUSTOM_ALTSHIP_BY_CLAUDE - the bucket's flag is the brand's
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — Freight-quoted broker
      // freight: the customer was billed a pass-through estimate, but we ship on
      // our own carrier and the broker invoices us after the fact, so our actual
      // freight cost is unknown until that invoice lands. Keep the Freight cost
      // box blank + show a pending banner until admin enters the real number
      // (which clears costSaved → banner self-hides). Order-level flag set by the
      // worker (q.freightCostPending) from lc.freightBalance.costPending.
      var freightCostPending = !_spAlt && !costSaved && (bId === 'broker' || bId === 'combined') && !!(q && q.freightCostPending); // CUSTOM_ALTSHIP_BY_CLAUDE
      var poNumber = bucket.poNumber || null;
      var poGenerated = !!poNumber;
      var requested = !!bucket.requestedAt;
      var requestedDate = bucket.requestedAt ? requestedStampHtml(bucket) : '';
      // CUSTOM_RFQPORTAL_BY_CLAUDE — requestKind drives which email
      // workflow this bucket fires. Defaults from bucket.type, but admin
      // can override via the ⚙️ popover (saved to wm.shippingBuckets[id]
      // .requestKind). 'none' skips the request row entirely.
      var requestKind = bucket.requestKind || (bucket.type === 'broker' ? 'dims' : 'freight');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Hyperice family default to 'none'.
         These vendors don't quote freight up front — they ship and then
         send us the freight invoice after the fact. So the "Freight
         quote requested from Hyperice" row reads as a false action
         item. Only the default flips here; if admin explicitly picked
         a kind via the cog popover (requestKindOverridden=true), respect
         that choice. */
      if (!bucket.requestKindOverridden && isPostShipInvoiceVendor(vendor)) {
        requestKind = 'none';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Web-order vendors
         (flagged 'web-order' in Vendor Guide) skip the request row too —
         admin buys direct on the vendor's website, no email handshake.
         Mirrors the Hyperice override above. Freight cost (when non-zero)
         still gets entered via the bucket cost input below. */
      var _vWf = (window.RPDash && window.RPDash.getVendorWorkflow) ? window.RPDash.getVendorWorkflow(vendor) : '';
      if (!bucket.requestKindOverridden && _vWf === 'web-order') {
        requestKind = 'none';
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var requiresDims = requestKind === 'dims';
      /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — ⛔ WILL CALL KEEPS THE ROW AND LOSES THE
         BUTTON. It is NOT `none`: `none` means there is nothing to ask and the row vanishes, which
         would also take away the only control that can put the bucket back. A collected bucket
         still has something to say — who is collecting it, and that nobody is emailing anybody —
         so the row stays, the picker stays switchable, and the send is what greys out. */
      var isWillCall = requestKind === 'will-call';
      var hasRequestRow = requestKind === 'dims' || requestKind === 'freight' || isWillCall;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-17) — recognize a PER-VENDOR request as a
         fallback for this bucket's request state. A dims/freight request made via the
         per-vendor handshake (lc.freightQuotes[vendor], e.g. the PURCHASING card before an
         order was promoted to Flow A on a manual freight-quoted flag) lives in a different
         subsystem than the bucket's own requestedAt. Without this, a promoted order shows
         "Email {vendor}" as if never requested even though it was (SHOP-1769). Gate by KIND so
         a dims request can't mark a freight bucket done (or vice-versa). On the next Re-send the
         bucket stamps its own requestedAt and takes over. */
      if (hasRequestRow && !requested) {
        var _vfq = (q.freightQuotes || {})[vendor] || {};
        var _vfqIsDims = _vfq.kind === 'dims';
        if (_vfq.requestedAt && (requiresDims ? _vfqIsDims : !_vfqIsDims)) {
          requested = true;
          requestedDate = requestedStampHtml(_vfq);
        }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      var vendorEsc = esc(vendor || '');
      var bucketAttrs = ' data-ref="' + esc(ref) + '" data-vendor="' + vendorEsc + '" data-bucket="' + esc(bId) + '"';
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) — PHASE 2: the same step/state contract
         the Purchasing card already emits (dashboard-po-lens.js `stepAttrs`), extended to this
         card because the two render the SAME fulfillment steps on different surfaces and only one
         of them was legible. Anything reading this card had to infer state from a ✓ glyph, an
         emoji or the presence of `rqd-toggle--done` — and ⛔ `--done` DOES NOT MEAN DONE here: a
         SCHEDULED PO (queued for the business-hours cron, not yet sent) carries it so the row sits
         in a green box while `done` stays false. A reader scanning for that class to answer "is
         the PO out?" gets true for a PO the vendor does not have.

         `data-step` names the step, `data-state` names where it is; both stay beside `data-vendor`,
         which remains the ROW'S OWN NAME (the key everything is stored under), never the resolved
         supplier — swap one and a freight cost saves under a name the gate cannot read back. */
      function stepAttrs(step, state) {
        return ' data-step="' + step + '" data-state="' + state + '"' + bucketAttrs;
      }

      // Gate: PO is only blocked by missing freight cost when the bucket
      // type requires it (parcel — supplier ships, needs cost on the PO
      // so they know what to charge us for shipping). Broker buckets
      // unlock PO immediately: supplier doesn't bill us for freight (our
      // broker does), and the rep generates the PO right away so
      // manufacturing can start — dims + final freight cost trickle in
      // post-manufacturing and don't gate the materials PO.
      var costGate = !!bucket.requiresCostBeforePo && !costSaved;
      // CUSTOM_ABBY_BY_CLAUDE (2026-06-02) — Flow A logical order: the PO comes
      // LAST, after the customer settles the freight balance. Gate the BROKER
      // bucket's PO until the balance is settled (paid / linked balance order /
      // handled-externally bypass) so the button shows grayed + unclickable
      // until then, matching the Tracking card's locked-button pattern. (The
      // "Mark balance handled externally" bypass is the escape for PO-early.)
      var _fbForGate = q.freightBalance || {};
      var _balanceSettled = !!(q.balanceCollected && q.balanceCollected.paid)
        || (Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0)
        || !!_fbForGate.handledExternally;
      /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — ⛔ A WILL-CALL BUCKET OWES NO BALANCE, so
         this gate could never open on one. Flow A holds the PO until the customer settles the
         freight balance we raise off our broker's quote; on a collected order we never quote a
         broker, never raise a balance, and nothing is ever collected — so the Generate PO button
         would sit greyed with "Collect the customer's freight balance first" for the life of the
         order. ⚠️ The ROLL-LENGTH gate below deliberately stays: cut lengths are a fact about the
         product, not about who carries it, and a roll cut wrong is wrong in the customer's van too. */
      var balanceGate = bucket.type === 'broker' && !_balanceSettled && requestKind !== 'will-call';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — also gate the PO on roll lengths: if any roll on the order
         has no length yet, gray out Generate PO so a PO can't reach the vendor without the cut
         lengths. Broker bucket only; clears once every roll has a length (already in there or
         added via the Roll lengths step). */
      var _rollsMissingLength = (Array.isArray(q.onlineLineItems) ? q.onlineLineItems : [])
        .some(function (it) { return it && it.isRoll && !(it.rollLayout && String(it.rollLayout).trim()); });
      var rollGate = bucket.type === 'broker' && _rollsMissingLength;
      var poGated = costGate || balanceGate || rollGate;
      var poGateReason = '';
      if (poGated) {
        if (balanceGate && !costSaved) poGateReason = 'Enter the freight cost above first.';
        else if (balanceGate) poGateReason = 'Collect the customer\'s freight balance first.';
        else if (costGate) poGateReason = 'Enter the supplier\'s freight cost first.';
        else if (rollGate) poGateReason = 'Add the roll length(s) first.';
      }

      var html = '<div class="rqd-fb__bucket" data-bucket="' + esc(bId) + '"' + _spAttr + '>';
      if (!_balOnly) { // CUSTOM_ALTSHIP_BY_CLAUDE - the shared bucket's balance row renders on its own
      /* CUSTOM_VENDORHEAD_BY_CLAUDE (Ryan 2026-08-29) — ⛔ THE HEADER ROW IS NO LONGER RENDERED
         JUST TO PARK THE ⚙️.

         The label and count already dropped out on a single-bucket order (see below), which left
         an empty full-width row whose only occupant was the cog — a band of dead space between
         the ECORE sub-header and the first workflow row, with the cog floating off the right edge
         of the button column. Ryan, with it boxed in red: *"there's a big awkward gap."*

         The cog now rides the request row beside the kind dropdown and Re-send, where it lines up
         with the button column instead of hanging past it, and this row is skipped entirely.

         ⛔ IT CANNOT ALWAYS RIDE THAT ROW. `requestKind: 'none'` renders no request row at all —
         and 'none' is reachable ONLY from inside this popover. Moving the cog unconditionally
         would strand every bucket set to 'none' with no way back to any of its settings. So the
         header returns, cog included, exactly when there is no request row to host it.

         ⚠️ It also returns for its ORIGINAL job whenever there are 2+ buckets, since then the
         labels are the only thing telling one panel from the next. In that case it carries the
         labels and the cog still rides the row. */
      var _bucketCount = (q && Array.isArray(q.shippingBuckets)) ? q.shippingBuckets.length : 1;
      var _cogRidesRow = hasRequestRow;
      var _noCog = _spAlt; // CUSTOM_ALTSHIP_BY_CLAUDE - the bucket's settings live on the brand's block
      /* One cog, two possible homes. `--inrow` gives it the 28px box + border of the controls it
         now sits between; `--done` matches the ghosted green those controls take once the request
         email has gone out, so the trio recedes together instead of the cog staying loud. */
      function settingsBtnHtml(extra) {
        return '<button type="button" class="rqd-fb__bucket-settings-btn' + (extra ? ' ' + extra : '')
          + '" data-bucket-action="open-settings" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId)
          + '" title="Bucket settings" aria-label="Bucket settings">&#9881;</button>';
      }
      if (!_noCog && (_bucketCount > 1 || !_cogRidesRow)) {
        html += '<div class="rqd-fb__bucket-header">';
      /* CUSTOM_VENDORHEAD_BY_CLAUDE (Ryan 2026-08-28) — the bucket label earns its place only when
         there is more than one bucket to tell apart.

         Ryan asked the right question: *"is there ever an instance where a single vendor has both
         'Broker freight' on it and whatever the opposite of Broker freight is?"* No, and it is
         guaranteed rather than incidental — `_isFreightQuoted` ORs a line's own state with
         `_fqVendors`, so if ANY line of a vendor is freight-quoted then EVERY line of that vendor
         is. A vendor's lines are all-broker or all-parcel, never split. This card also refuses to
         render above a single vendor (multi-vendor falls back to a notice), so one bucket is the
         normal case and "Broker freight · 1 item" was labelling a set of one, directly under a
         vendor name that had already said whose shipment it is.

         ⛔ THE HEADER ROW ITSELF STAYS. The ⚙️ lives in it — request kind, rename, combine/split —
         and hiding the row to remove a redundant word would take the only route to those settings
         with it. Only the label and count drop out; the cog keeps its position.

         Two or more buckets (an admin-made custom bucket, or a genuine broker + parcel split across
         a mixed cart) and the labels come back, because then they are the only thing distinguishing
         one panel from the next. */
      var _bucketCount = (q && Array.isArray(q.shippingBuckets)) ? q.shippingBuckets.length : 1;
      if (_bucketCount > 1) {
        html += '<span class="rqd-fb__bucket-label">' + bLabel + '</span>';
        html += '<span class="rqd-fb__bucket-count">' + bucket.lineItemCount + ' item' + (bucket.lineItemCount === 1 ? '' : 's') + '</span>';
      } else {
        /* The count still has to hold the ⚙️ over to the right — it carries `margin-right:auto`.
           An empty span keeps that layout job without printing a redundant "1 item". */
        html += '<span class="rqd-fb__bucket-count"></span>';
      }
      // ⚙️ settings trigger — opens a popover with request kind picker,
      // rename label, and combine/split toggle.
        if (!_cogRidesRow) html += settingsBtnHtml('');
        html += '</div>';
      }

      // ── Settings popover ──
      // Hidden by default; toggled by the ⚙️ button. Contains the
      // request-kind picker, rename input, and combine/split toggle.
      // Combine appears in auto-split mode; Split appears in combined.
      var bucketsMode = q && q.shippingBucketsMode === 'combined' ? 'combined' : 'auto-split';
      var kindOpts = [
        { v: 'dims',      label: 'Weight/dims request' },
        { v: 'freight',   label: 'Freight quote request' },
        /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — offered here as well as on the row,
           because both surfaces render off the same requestKind and write through the same
           endpoint; a choice on one that the other cannot show is how they drift. */
        { v: 'will-call', label: 'Will Call — customer collects' },
        { v: 'none',      label: 'None — no request needed' },
      ];
      /* Built into its own string rather than straight into `html`: it has to be emitted next to
         whichever element opens it, and that is now two different places. It stays INSIDE
         .rqd-fb__bucket either way, which is all handleBucketSettingsToggle needs — it resolves
         the panel with bucketEl.querySelector, not a fixed path. */
      var popoverHtml = '';
      popoverHtml += '<div class="rqd-fb__bucket-popover' + (_cogRidesRow ? ' rqd-fb__bucket-popover--inrow' : '')
        + '" hidden data-bucket-popover="' + esc(bId) + '" data-ref="' + esc(ref) + '">';
      popoverHtml += '<div class="rqd-fb__bucket-popover-row">';
      popoverHtml += '<label class="rqd-fb__bucket-popover-label">Request kind</label>';
      popoverHtml += '<select class="rqd-fb__bucket-popover-kind" data-bucket-action="change-kind" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '">';
      for (var ki = 0; ki < kindOpts.length; ki++) {
        var ko = kindOpts[ki];
        var sel = (ko.v === requestKind) ? ' selected' : '';
        popoverHtml += '<option value="' + ko.v + '"' + sel + '>' + ko.label + '</option>';
      }
      popoverHtml += '</select>';
      popoverHtml += '</div>';
      popoverHtml += '<div class="rqd-fb__bucket-popover-row">';
      popoverHtml += '<label class="rqd-fb__bucket-popover-label">Label</label>';
      popoverHtml += '<input type="text" class="rqd-fb__bucket-popover-label-input" data-bucket-action="rename" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '" value="' + bLabel + '" maxlength="60" placeholder="e.g. ' + (bucket.type === 'broker' ? 'Broker freight' : (bucket.type === 'parcel-supplier' ? 'Supplier parcel' : 'Shipping')) + '">';
      popoverHtml += '</div>';
      if (bucketsMode === 'auto-split') {
        popoverHtml += '<button type="button" class="rqd-fb__bucket-popover-mode" data-bucket-action="combine" data-ref="' + esc(ref) + '">Combine into one bucket</button>';
      } else {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Gray out Split when there are no
           freight-quoted lines in the order. Auto-split mode would produce
           the same single (parcel) bucket since there's nothing to peel
           into a broker bucket — so the click would be a no-op and
           confusing. Hint at the remediation in the title. */
        /* CUSTOM_AUTOFREIGHT_BY_CLAUDE (Ryan 2026-08-29) — through the shared predicate, so the
           weight rule counts. Before this, combining a weight-rule order left Split greyed out
           with a tooltip telling admin to flag a line that was ALREADY freight — a one-way door
           out of combined mode on exactly the orders the rule exists for. */
        var _hasFqLinesForSplit = Array.isArray(q.onlineLineItems) && q.onlineLineItems.some(function (it) {
          return lineIsFreightQuoted(it, q);
        });
        if (_hasFqLinesForSplit) {
          popoverHtml += '<button type="button" class="rqd-fb__bucket-popover-mode" data-bucket-action="split" data-ref="' + esc(ref) + '">Split back into broker + parcel</button>';
        } else {
          popoverHtml += '<button type="button" class="rqd-fb__bucket-popover-mode rqd-fb__bucket-popover-mode--disabled" disabled aria-disabled="true" title="No freight-quoted lines to split off. Flag at least one line via the + button on the Order Contents card first.">Split back into broker + parcel</button>';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      }
      popoverHtml += '</div>';
      /* Header-cog case: the panel keeps its original anchoring (absolute, top-right of the
         bucket). The in-row case emits it inside the button group instead, where it can hang off
         the cog itself. */
      if (!_cogRidesRow) html += popoverHtml;

      // ── Request row ── (only when requestKind ∈ {dims, freight}; 'none' skips)
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-23) — the dims-vs-freight-quote
         choice now sits ON the row as a dropdown, not only behind the ⚙ popover. A
         broker bucket defaults to a weight/dims ask (we quote it with our own broker
         and ship on our carrier), which is right ~9 times out of 10 — but on small,
         parcel-size orders the supplier's own parcel rate usually beats our broker's,
         and nothing on the card hinted the ask was switchable. Same state and same
         endpoint as the ⚙ picker; both surfaces render off requestKind.
         Lives INSIDE .rqd-invoice (which carries margin-left:auto) so it shares the
         right edge with Email {vendor} / Generate PO, and is sized to the same
         140×28 footprint as those buttons. */
      if (hasRequestRow) {
        // Un-requested reads as the open question ("Request from ECORE"); once sent
        // it reads as the specific thing that was asked for.
        var requestLabel = isWillCall
          /* CUSTOM_WILLCALL_BY_CLAUDE — a statement, not an open question: nothing is being asked
             for, so "Request from Legend" would read as an action item that never gets done. */
          ? 'Customer collecting from ' + vendorEsc
          : (requested
            ? (requiresDims
                ? 'Weight/dims requested from ' + vendorEsc
                : 'Freight quote requested from ' + vendorEsc)
            : 'Request from ' + vendorEsc);
        var requestAction = requiresDims ? 'request-dims' : 'request-quote';
        var requestDataAttr = requiresDims ? 'data-fb-action' : 'data-freight-action';
        /* CUSTOM_WILLCALL_BY_CLAUDE — a collected bucket is SETTLED, not outstanding, so it takes
           the house "n/a" treatment the AR/AP card set: the green --done row with a lowercase n/a
           in the check slot rather than a tick, because a tick would claim we asked this vendor
           for something. Without this the row sits un-ticked forever and reads as a job to do. */
        html += '<div class="rqd-toggle' + (requested || isWillCall ? ' rqd-toggle--done' : '') + '"'
          + stepAttrs(requestKind === 'dims' ? 'weight-dims-received' : 'freight-quote',
            isWillCall ? 'not-applicable' : (requested ? 'requested' : 'not-requested')) + '>';
        html += '<div class="rqd-toggle__check' + (isWillCall ? ' rqd-toggle__check--na' : '') + '" style="cursor:default;">'
          + (isWillCall ? 'n/a' : (requested ? '&#10003;' : '')) + '</div>';
        html += '<span>' + requestLabel + '</span>';
        if (requested) html += '<span class="rqd-toggle__date">' + requestedDate + '</span>';
        /* A request already sent under the OTHER kind. Flipping correctly re-opens
           the row (a dims ask doesn't satisfy a freight ask), but without this note
           the sent email vanishes off the card and reads as "did it not send?".
           Can coexist with the date above — ask dims, flip, ask freight. */
        var _priorReq = bucket.priorRequest;
        if (_priorReq && _priorReq.requestedAt) {
          html += '<span class="rqd-kindprior">'
            + (_priorReq.kind === 'dims' ? 'weight/dims' : 'freight quote')
            + ' already requested ' + esc(formatDate(_priorReq.requestedAt)) + '</span>';
        }
        html += '<div class="rqd-invoice">';
        /* CUSTOM_VENDORHEAD_BY_CLAUDE (Ryan 2026-08-29) — the ⚙️, in the row.

           ⛔ FIRST IN THE GROUP, NOT LAST. `.rqd-invoice` is `margin-left:auto`, so it is pinned
           by its RIGHT edge — the edge every other workflow row's buttons line up on. Appending
           the cog would push this row 36px further right than Log dims / Balanced / Re-gen and
           break the one alignment the card has. Leading, the group simply grows leftward and the
           right edge never moves.

           The popover is emitted inside the wrapper so it can hang off the cog itself rather than
           off the card corner; the wrapper is the positioning context. */
        if (!_noCog) {
        html += '<span class="rqd-fb__bucket-settings-wrap">'
          + settingsBtnHtml('rqd-fb__bucket-settings-btn--inrow' + (requested ? ' rqd-fb__bucket-settings-btn--done' : ''))
          + popoverHtml
          + '</span>';
        }
        /* Kind picker. Only the two real asks are offered here — 'none' stays in the
           ⚙ popover, since picking it from this control would delete the row the
           control lives on. Fires `change`, handled by the same delegated listener
           as the popover's select (both carry data-bucket-action="change-kind").
           No `title` — the row label already says what this is, and the long
           explanatory tooltip was just noise on hover (Ryan 2026-07-23). */
        /* Once the request email has actually gone out, the picker recedes to the same
           ghosted done-state as the Re-send button beside it (transparent fill, dark-green
           outline + ink) — it's still switchable, just no longer competing for attention.
           The class rides the shared .rqd-invoice__generate rules. (Ryan 2026-07-23) */
        /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) — the bucket request row's release pill,
           between the cog and the kind picker. ⚠️ It could not have rendered before today: the
           worker's bucket derivation built its request object from an explicit field list that
           never carried `scheduledFor`, so this row had no idea a held email existed. Fixed in
           the same change (handlers/quote-dashboard.js `_bucketRequest`). */
        html += scheduledReleaseBtnHtml({
          kind: 'freight', ref: ref, vendor: vendor,
          type: (requestKind === 'dims' ? 'dims' : 'freight'),
          bucket: _spAlt ? '' : bId, scheduledFor: bucket.scheduledFor, // CUSTOM_ALTSHIP_BY_CLAUDE - an alternate's held ask has no bucket
        });
        html += '<select class="rqd-kindselect' + (requested ? ' rqd-kindselect--done' : '') + '" data-bucket-action="change-kind"'
          + ' data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '"' + _spAttr + '>';
        html += '<option value="dims"' + (requestKind === 'dims' ? ' selected' : '') + '>Weight/dims</option>';
        html += '<option value="freight"' + (requestKind === 'freight' ? ' selected' : '') + '>Freight</option>';
        /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — the third choice. SHOP-1923 is the order
           that asked for it: the customer wanted to collect from Legend's dock. */
        html += '<option value="will-call"' + (isWillCall ? ' selected' : '') + '>Will Call</option>';
        html += '</select>';
        if (isWillCall) {
          /* ⛔ DISABLED, NOT REMOVED. The button keeps its place so the row does not reflow as the
             picker changes, and the title says WHY rather than leaving a dead control to poke at.
             `disabled` carries `aria-disabled` for the same reason the Split button above does. */
          html += '<button class="rqd-invoice__link rqd-invoice__generate rqd-fb-btn--disabled" type="button" disabled aria-disabled="true"'
            + ' title="The customer is collecting this from ' + vendorEsc + ' — there is no freight or weight to ask for.">Email ' + esc(shortVendorLabel(vendor)) + '</button>';
        } else if (requested) {
          html += '<button class="rqd-invoice__link rqd-invoice__generate" type="button" ' + requestDataAttr + '="' + requestAction + '"' + bucketAttrs + ' title="Re-open the modal to review & re-send the request email">Re-send</button>';
        } else {
          html += '<button class="rqd-invoice__link rqd-fb-btn--primary" type="button" ' + requestDataAttr + '="' + requestAction + '"' + bucketAttrs + ' title="Email ' + vendorEsc + '">Email ' + esc(shortVendorLabel(vendor)) + '</button>';
        }
        html += '</div>';
        html += '</div>';
      }

      // ── Dims received row (only when requestKind === 'dims') ──
      // Always editable — admin enters FINAL post-manufacturing dims
      // here (the data we want for COGS + future freight automation),
      // independently of whether the dashboard's request email was fired
      // (admin may receive dims via reply or other means). When dims
      // aren't yet entered, the inline pallet input form is shown
      // immediately; when entered, "✓ + dims display + Edit".
      if (requiresDims) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — dims N/A (supplier
           shipped, no dims forthcoming) is order-level on freightBalance.dims. */
        var _dimsNa = !!(q.freightBalance && q.freightBalance.dims && typeof q.freightBalance.dims === 'object' && q.freightBalance.dims.na);
        /* CUSTOM_ALTSHIP_BY_CLAUDE - an alternate source's dims are its own (lifecycle.ordering[name]); N/A there
           shows done with no Undo, because the Undo here clears the order-level dims, which are the brand's. */
        var _spDims = _spAlt ? (_sp.dims || null) : null;
        if (_spAlt) _dimsNa = !!(_spDims && typeof _spDims === 'object' && _spDims.na);
        if (_dimsNa && _spAlt) {
          html += '<div class="rqd-toggle rqd-toggle--done"' + stepAttrs('weight-dims-received', 'not-applicable') + '>'
            + '<div class="rqd-toggle__check rqd-toggle__check--na" style="cursor:default;">n/a</div>'
            + '<span>Weight/dims received</span><span class="rqd-toggle__date">N/A</span></div>';
        } else if (_dimsNa) {
          html += renderDimsNaDoneRow(ref, (q.freightBalance && q.freightBalance.dims) || null);
        } else if (dimsEntered) {
          var displayDims = dimsSummary || dimsRaw || '';
          html += '<div class="rqd-toggle rqd-toggle--done"'
            + stepAttrs(requestKind === 'dims' ? 'weight-dims-received' : 'freight-quote', 'received') + '>';
          html += '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>';
          html += '<span>Weight/dims received</span>';
          html += '<span class="rqd-toggle__date">' + esc(displayDims) + '</span>';
          html += '<div class="rqd-invoice" style="margin-left:auto;">';
          html += '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="edit-dims" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '"' + _spAttr + '>Edit</button>';
          html += '</div>';
          html += '</div>';
          if (_spAlt) html += '<div class="rqd-bucket-dims-block" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '"' + _spAttr + '></div>'; // CUSTOM_ALTSHIP_BY_CLAUDE - where its Edit opens
        } else {
          /* CUSTOM_DIMSDISCLOSURE_BY_CLAUDE (Ryan 2026-08-28) — ⛔ THE FORM NO LONGER OPENS ITSELF.
             It used to render immediately here ("rep can enter dims any time"), so every online
             deal waiting on a supplier showed a pallet count, four dimension boxes and four
             buttons for a measurement nobody has yet.

             Ryan, comparing it with the offline card: *"I like this more minimalistic setup where
             those buttons only appear if you click the Log dims button first and then they'll stay
             around if you actually log and save anything, otherwise they don't need to be visible
             while you wait for the dims to be sent to us."*

             Same shape the offline card already uses: a collapsed row with a disclosure button,
             and an empty sibling block the handler fills in place. No re-render, no state flag.

             ⚠️ Its own action + container, NOT the offline `add-vendor-dims`. That one fills a
             `.rqd-vendor-dims-block[data-vendor]` and builds the form WITH a vendor, which routes
             the save to lifecycle.ordering[vendor].freightDims. This surface is order-level and
             must reach freightBalance.dims — passing a vendor here would file an online order's
             dims somewhere the Fulfillment card never reads them back from. */
          /* `not-received`, matching this card's negated-pair vocabulary (requested/not-requested,
             recorded/not-recorded). Ryan is training a bot to read these attributes, so an
             ad-hoc third word for "not done yet" would be one more thing it has to be taught.
             ⚠️ This state emitted NO data-step at all before — the form it rendered carried none —
             so the pending step was invisible to anything reading the DOM. */
          html += '<div class="rqd-toggle"'
            + stepAttrs(requestKind === 'dims' ? 'weight-dims-received' : 'freight-quote', 'not-received') + '>';
          html += '<div class="rqd-toggle__check" style="cursor:default;"></div>';
          html += '<span>Weight/dims received</span>';
          html += '<div class="rqd-invoice" style="margin-left:auto;">';
          html += '<button type="button" class="rqd-invoice__link" data-fb-action="open-bucket-dims" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '"' + _spAttr + ' title="Log the pallet count, weight and dimensions once the supplier sends them back.">Log dims</button>';
          html += '</div>';
          html += '</div>';
          html += '<div class="rqd-bucket-dims-block" data-ref="' + esc(ref) + '" data-bucket="' + esc(bId) + '"' + _spAttr + '></div>';
        }
      }

      /* CUSTOM_WILLCALLCOST_BY_CLAUDE (Ryan 2026-10-26) - a Will Call shipment has no carrier, so no freight cost to
         enter: the row (and its pending banners) is hidden. A cost ALREADY saved stays visible, labelled as saved before
         will-call, so it can be cleared rather than sit unseen against the deal's profit. */
      var _wcHideCost = isWillCall && !costSaved;
      if (!_wcHideCost) {
      // ── Freight cost row ──
      /* CUSTOM_FBCOSTSAVE_BY_CLAUDE (Ryan 2026-08-13) — input + explicit SAVE button, built from
         the Purchasing card's freight-quote cluster (.rqd-freight-cost-wrap / -input-wrap /
         -input / -save, rendered by dashboard-po-lens.js). Ryan: *"the save button and text input
         box should be just like how they are on the PURCHASING card on the freight quote line."*

         It reuses that cluster's CLASSES rather than copying its CSS, so the 3/4-input +
         1/4-floppy proportions, the focus ring and the SOLD-scope green fill are defined once and
         cannot drift between the two cards.

         WHY IT CHANGED. The field used to auto-save on blur — Ryan: *"it's not clear if you need
         to press enter, click outside the box, just sit there and wait."* A field that commits on
         an ambiguous event has no moment where the rep knows the number is in. Now typing alone
         does nothing and the floppy is the only commit, matching every other saved field here.

         .rqd-fb__bucket-cost-input stays on the input: /freight-cost-focus deep-links to it and
         the parcel-pending scroll target queries it by that name. */
      html += '<div class="rqd-toggle' + (costSaved ? ' rqd-toggle--done' : '') + '"'
        + stepAttrs('freight-cost', costSaved ? 'recorded' : 'not-recorded') + '>';
      html += '<div class="rqd-toggle__check" style="cursor:default;">' + (costSaved ? '&#10003;' : '') + '</div>';
      html += '<span>' + (isWillCall ? 'Freight cost (saved before will-call)' : 'Freight cost') + '</span>';
      html += '<div class="rqd-freight-cost-wrap" title="Required before order close so the Financials card freight cost is accurate. Type the amount, then press the save icon.">';
      html += '<div class="rqd-freight-cost-input-wrap">';
      /* CUSTOM_FBCOSTSAVE_BY_CLAUDE (Ryan 2026-08-31) — ⛔ THE SECOND CLASS IS LOAD-BEARING.
         dashboard.css has carried `.rqd-toggle--done .rqd-fb__bucket-cost-prefix` since the
         ghost flip shipped, to darken the "$" in step with the number once the cost is saved —
         but this span only ever rendered `.rqd-freight-cost-prefix`, so that rule matched
         nothing and the sold-scope pale-green `#bbf7d0` stayed on the "$" while the input's
         text went dark `#052e16`. Ryan: *"the '$' in the text input field is a different font
         color to the '5.00'."* The input carries both class names for exactly this reason
         (.rqd-freight-cost-input + .rqd-fb__bucket-cost-input); the prefix was missing its twin.
         Scoped rather than re-pointing the CSS at `.rqd-freight-cost-prefix`, because the
         Purchasing card shares that class and must keep its own treatment. */
      html += '<span class="rqd-freight-cost-prefix rqd-fb__bucket-cost-prefix">$</span>';
      html += '<input type="text" inputmode="decimal" class="rqd-freight-cost-input rqd-fb__bucket-cost-input"'
        + ' data-bucket="' + esc(bId) + '" data-ref="' + esc(ref) + '"' + _spAttr
        + ' value="' + costStr + '" data-last-saved="' + costStr + '"'
        + (parcelRatePending ? ' data-parcel-pending="1"' : '')
        + ' placeholder="0.00" aria-label="Bucket freight cost">';
      html += '</div>';
      /* CUSTOM_FBCOSTSAVE_BY_CLAUDE (Ryan 2026-09-01) — ⛔ THE SECOND CLASS IS LOAD-BEARING HERE
         TOO, for the same reason it is on the input above. The done-state ghost flip added
         2026-08-31 was scoped to `.rqd-toggle--done .rqd-freight-cost-save`, and `--done` does not
         mean the same thing on every card that renders this button: here it means the cost is
         saved (nothing left to do → ghost), but on the PURCHASING card's freight-handshake row it
         means the REQUEST went out and we are still waiting for the supplier's number — where the
         floppy is the next action and was being ghosted out. The twin class ties the flip to this
         row's own meaning of done. */
      html += '<button type="button" class="rqd-invoice__link rqd-freight-cost-save rqd-fb__bucket-cost-save"'
        + ' data-fb-action="save-bucket-cost" data-bucket="' + esc(bId) + '" data-ref="' + esc(ref) + '"' + _spAttr
        + ' style="cursor:pointer;" title="Save freight cost" aria-label="Save freight cost">'
        + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
        + '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>'
        + '<polyline points="17 21 17 13 7 13 7 21"></polyline>'
        + '<polyline points="7 3 7 8 15 8"></polyline></svg></button>';
      html += '</div>';
      html += '</div>';
      // ⚠ Pending parcel invoice banner — appears below the Freight cost
      // row when the PO was generated against a parcel-only vendor (PO
      // body says "Per supplier's standard parcel rates") AND admin
      // hasn't entered the actual cost yet. Entering ANY value here (incl.
      // $0 when vendor didn't bill us) clears the flag and unlocks the
      // Freight Balanced step.
      if (parcelRatePending) {
        var pendingSince = bucket.parcelRatePending && bucket.parcelRatePending.since
          ? formatDate(bucket.parcelRatePending.since) : '';
        var pendingVendor = bucket.parcelRatePending && bucket.parcelRatePending.vendorName
          ? esc(bucket.parcelRatePending.vendorName) : 'supplier';
        html += '<div class="rqd-fb__parcel-pending" style="margin:6px 14px 4px;padding:8px 10px;border-radius:6px;background:rgba(217,119,6,0.10);border-left:3px solid #d97706;font-size:12px;line-height:1.4;color:var(--rq-text-muted,#667085);">';
        html += '<strong style="color:#d97706;">⚠ Pending parcel invoice</strong> — ' + pendingVendor + ' bills standard parcel rate separately. Enter their freight cost above when the invoice arrives (or $0 if no charge). ';
        if (pendingSince) html += '<span style="opacity:0.75">PO sent ' + pendingSince + '.</span>';
        html += '</div>';
      }
      // ⚠ Broker cost pending banner — freight-quoted order whose customer freight
      // balance was charged (pass-through estimate) but whose actual broker cost
      // arrives after we ship. Mirrors the parcel-pending banner; clears itself
      // once admin types the real cost above (costSaved flips → freightCostPending
      // is gated on !costSaved).
      if (freightCostPending) {
        html += '<div class="rqd-fb__freight-cost-pending" style="margin:6px 14px 4px;padding:8px 10px;border-radius:6px;background:rgba(217,119,6,0.10);border-left:3px solid #d97706;font-size:12px;line-height:1.4;color:var(--rq-text-muted,#667085);">';
        html += '<strong style="color:#d97706;">⚠ Broker cost pending</strong> — freight-quoted: the customer was charged a pass-through estimate, but our actual broker freight cost arrives after we ship. Enter it above when the broker invoices you (the box is intentionally blank until then).';
        html += '</div>';
      }
      } // end !_wcHideCost (CUSTOM_WILLCALLCOST_BY_CLAUDE)

      } // end !_balOnly (CUSTOM_ALTSHIP_BY_CLAUDE)

      // CUSTOM_ABBY_BY_CLAUDE (2026-06-02) — Customer balance row. Positioned
      // between Freight cost and the PO row per the logical flow: the customer
      // pays the balance + the separate order is linked, THEN we send the PO.
      // Broker bucket + non-post-ship vendor only (the customer-pays-freight
      // case). Reuses the order-level freight-balance state + the generate →
      // Abby-email-modal flow (data-fb-action handlers in dashboard-main.js).
      if (bucket.type === 'broker' && !isPostShipInvoiceVendor(vendor) && (!_shared || _balOnly)) { // CUSTOM_ALTSHIP_BY_CLAUDE - one per order
        var _cbFb = q.freightBalance || {};
        var _cbBc = q.balanceCollected || {};
        var _cbInvoiced = !!_cbFb.draftOrderId;
        var _cbEmailed = !!_cbFb.emailedAt;
        var _cbPaid = !!_cbBc.paid;
        var _cbLinked = Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0;
        var _cbPaidEff = _cbPaid || _cbLinked;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — "handled externally" ALSO completes this
           step. Without it the row stayed unchecked even though the top-of-card bypass banner said
           the balance was handled (and the PO gate had cleared) — an inconsistency Ryan flagged.
           Now it shows a checkmark + date/actor, matching every other completed step. */
        var _cbExtRaw = _cbFb.handledExternally;
        var _cbExt = _cbExtRaw ? (typeof _cbExtRaw === 'object' ? _cbExtRaw : {}) : null;
        var _cbDone = _cbInvoiced || _cbPaidEff || !!_cbExt;
        html += '<div class="rqd-toggle' + (_cbDone ? ' rqd-toggle--done' : '') + '"'
          + stepAttrs('customer-freight-balance', _cbDone ? 'settled' : 'outstanding') + '>';
        html += '<div class="rqd-toggle__check" style="cursor:default;">' + (_cbDone ? '&#10003;' : '') + '</div>';
        html += '<span>Customer balance</span>';
        if (_cbExt) {
          /* CUSTOM_NOBALANCEWORDS_BY_CLAUDE (Ryan 2026-10-09) - "handled externally" read as if somebody collected the
             money elsewhere; the No Balance click means there is nothing to collect. */
          html += '<span class="rqd-toggle__date">No balance to collect' + (_cbExt.at ? ' - ' + formatDate(_cbExt.at) : '') + (_cbExt.by ? ' · ' + esc(_cbExt.by) : '') + '</span>';
          /* CUSTOM_BALANCEBTN_BY_CLAUDE (Ryan 2026-08-28) — the DONE state of the No Balance button,
             standing where it stood. Ryan: *"we get a banner at the top for 'Balance handled
             externally — 8/28/2026 — by RW' with a 'Clear override' button on the far right. This
             banner doesn't need to exist. We can just get a ghosted out button taking the place of
             the 'No balance' button which changes to 'Balanced' or something. And then clicking on
             that can give us the option to undo."*

             A whole banner across the top of the card to say one row is settled was outsized for
             what it reported, and it said it a second time — the row already carries "handled
             externally · date · who" in its own date slot. The ghost variant
             (.rqd-invoice__generate) is this card's established "done, still available" treatment,
             the same one Re-send and Re-gen use. Clicking still routes to bypass-clear, which
             confirms before undoing. */
          if (isAdmin) {
            html += '<div class="rqd-invoice" style="margin-left:auto;">';
            html += '<button type="button" class="rqd-invoice__link rqd-invoice__generate" data-fb-action="bypass-clear" data-ref="' + esc(ref) + '" title="No customer balance was owed on this order. Click to undo — the Flow A invoice gate re-engages until Invoice + Email completes.">Balanced</button>';
            html += '</div>';
          }
        } else if (_cbPaidEff) {
          if (_cbLinked && !_cbPaid) {
            var _cbChildRefs = (q.balanceChildren || []).map(function (c) { return esc(c.reference || c.orderName || ''); }).filter(Boolean).join(', ');
            html += '<span class="rqd-toggle__date">paid via ' + _cbChildRefs + '</span>';
          } else {
            html += '<span class="rqd-toggle__date">paid' + (_cbBc.paidAt ? ' ' + formatDate(_cbBc.paidAt) : '') + '</span>';
          }
        } else if (_cbInvoiced) {
          var _cbChargeStr = money(_cbFb.customerCharge != null ? _cbFb.customerCharge : (_cbFb.brokerCost || 0));
          html += '<span class="rqd-toggle__date">' + (_cbEmailed ? ('emailed ' + formatDate(_cbFb.emailedAt)) : 'invoice ready') + ' · ' + _cbChargeStr + ' charged</span>';
          html += '<div class="rqd-invoice" style="margin-left:auto;">';
          if (_cbFb.invoiceUrl) html += '<a class="rqd-invoice__link rqd-invoice__generate" href="' + esc(_cbFb.invoiceUrl) + '" target="_blank" title="Opens the freight balance draft order' + (_cbFb.draftOrderName ? ' (' + esc(_cbFb.draftOrderName) + ')' : '') + '">&#128279; BALANCE</a>';
          // After the email's actually been sent, ghost the Resend button so it
          // reads as "done, but available" rather than a prominent green action.
          html += '<button class="rqd-invoice__link" data-fb-action="send-email" data-ref="' + esc(ref) + '"' + (_cbEmailed ? ' style="opacity:0.5;"' : '') + ' title="Re-open the Abby email preview using the existing draft — no duplicate created.">' + (_cbEmailed ? 'Resend Email' : 'Send Email') + '</button>';
          // (Edit button removed 2026-06-02 — editing the Freight cost line above
          // now re-prices this balance draft directly via /shipping-bucket-set.)
          html += '<button class="rqd-invoice__link" data-fb-action="mark-paid" data-ref="' + esc(ref) + '" title="Fallback — the orders/paid webhook normally flips this automatically when the customer pays.">Mark Paid</button>';
          html += '</div>';
        } else {
          // No invoice yet — NO duplicate input. The charge is pulled from the
          // Freight cost entered above (pass-through). Show what will be charged
          // + the Invoice + Email button; gate it until a freight cost exists.
          // (If the balance ever needs more than freight — e.g. an added
          // adhesive — bump it via Edit after generating, so the freight P&L
          // above stays clean.)
          // Visible-but-locked until the freight cost is entered above (Ryan
          // 2026-06-02): show the Invoice + Email button grayed + unclickable
          // rather than hiding it, matching the Tracking card's locked pattern.
          var _cbReady = costSaved && parseFloat(costStr) > 0;
          /* CUSTOM_ALTSHIP_BY_CLAUDE - on a shared bucket the view's cost IS the sum of every supplier's shipment,
             ready only once every one of them is saved; the customer paid once. */
          if (_cbReady) html += '<span class="rqd-toggle__date">will charge ' + money(parseFloat(costStr)) + (_shared && _shared.count > 1 ? ' (' + _shared.count + ' shipments)' : '') + '</span>';
          html += '<div class="rqd-invoice" style="margin-left:auto;">';
          if (_cbReady) {
            html += '<button class="rqd-invoice__link rqd-fb-btn--primary" data-fb-action="generate-invoice" data-ref="' + esc(ref) + '" title="Creates the Shopify draft for the freight cost above and opens the Abby email to the customer.">Invoice + Email</button>';
          } else {
            html += '<button class="rqd-invoice__link rqd-invoice__link--disabled" disabled title="Enter the freight cost above first.">Invoice + Email</button>';
          }
          /* CUSTOM_BALANCEBTN_BY_CLAUDE (Ryan 2026-08-28) — the other way to settle this row: no
             separate balance is owed, so nothing is invoiced and nothing is emailed. SHOP-1996 is
             the case — the customer already paid freight at checkout and Ryan does not intend to
             bill more.

             ⛔ LABELLED "No Balance", NOT "Mark Paid" AS RYAN SUGGESTED, and the reason is one he
             could not have seen: THIS CARD ALREADY HAS A "Mark Paid" BUTTON a few lines up
             (data-fb-action="mark-paid"), which records that the customer PAID an invoice that was
             actually issued. Two buttons reading "Mark Paid" on one card, doing different things —
             one settles a real invoice, the other declares there was never a balance — is a
             mis-click that ends with a customer either billed or not billed by accident. They sit
             in mutually exclusive branches so they never co-render, which is exactly what would
             make the collision hard to notice until it mattered. Trivial to change back if he
             wants the shorter word.

             Renders for admins only, as the top-of-card button did. */
          if (isAdmin) {
            html += '<button type="button" class="rqd-invoice__link" data-fb-action="bypass-set" data-ref="' + esc(ref) + '" title="No separate customer balance is owed — e.g. they already paid enough freight at checkout and we ship on our own carrier, the balance was settled outside Flow A, or a balance child was not linked. Unlocks Generate PO WITHOUT the invoice + email step; nothing is sent to the customer. Record the actual freight cost above for your records.">No Balance</button>';
          }
          html += '</div>';
        }
        html += '</div>';
      }

      /* ── Roll lengths row ── (CUSTOM_RFQPORTAL_BY_CLAUDE) Sits between Customer
         Balance and Purchase Order. Surfaces when the order has roll items; "done"
         when every roll has a length (calculator "Roll Layout" property OR a rep-
         entered override). Lets the rep log roll lengths that flow to the PO for rolls
         bought online without the flooring calculator. Broker bucket only (rolls ship
         freight); non-blocking — the PO's own missing-roll confirm stays as backstop. */
      if (bucket.type === 'broker' && (!_shared || _balOnly)) { // CUSTOM_ALTSHIP_BY_CLAUDE - once per bucket
        var _rlRolls = (Array.isArray(q.onlineLineItems) ? q.onlineLineItems : []).filter(function (it) { return it && it.isRoll; });
        if (_rlRolls.length) {
          var _rlMissing = _rlRolls.filter(function (it) { return !(it.rollLayout && String(it.rollLayout).trim()); });
          var _rlDone = _rlMissing.length === 0;
          html += '<div class="rqd-toggle' + (_rlDone ? ' rqd-toggle--done' : '') + '"'
            + stepAttrs('roll-lengths', _rlDone ? 'recorded' : 'not-recorded') + '>';
          html += '<div class="rqd-toggle__check" style="cursor:default;">' + (_rlDone ? '&#10003;' : '') + '</div>';
          html += '<span>Roll lengths</span>';
          if (_rlDone) {
            var _rlLens = _rlRolls.map(function (it) { return String(it.rollLayout || '').trim(); }).filter(Boolean).join('; ');
            var _rlAt = q.rollLengthsLoggedAt ? formatDate(q.rollLengthsLoggedAt) : '';
            html += '<span class="rqd-toggle__date">' + (_rlLens ? esc(_rlLens) : '') + (_rlAt ? ' – ' + esc(_rlAt) : '') + '</span>';
          }
          html += '<div class="rqd-invoice" style="margin-left:auto;">';
          if (_rlDone) {
            // Ghosted (done-state) config — same as the Re-gen / Re-send buttons.
            html += '<button class="rqd-invoice__link rqd-invoice__generate rqd-roll-lengths-btn" data-ref="' + esc(ref) + '" type="button">Edit roll lengths</button>';
          } else {
            // Forest-green (input-required) config + "Add roll lengths".
            html += '<button class="rqd-invoice__link rqd-fb-btn--primary rqd-roll-lengths-btn" data-ref="' + esc(ref) + '" type="button" style="cursor:pointer;">Add roll lengths</button>';
          }
          html += '</div>';
          html += '</div>';
        }
      }

      // ── PO row ── (skipped on single-bucket no-request workflows —
      //   parcel-rate-only orders like Hyperide are 1 bucket + 1 vendor
      //   + no email-request step, so the bucket panel's PO row would
      //   exactly duplicate the order's Purchasing card. Skip here and
      //   defer to Purchasing. Multi-bucket orders (broker + parcel) keep
      //   the per-bucket PO row because each bucket has its own PO that
      //   the Purchasing card can't disambiguate.)
      var _singleBucketNoRequest = !_sp && requestKind === 'none' && Array.isArray(q.shippingBuckets) && q.shippingBuckets.length === 1;
      if (_singleBucketNoRequest || _balOnly) { // CUSTOM_ALTSHIP_BY_CLAUDE - the balance-only pass ends here
        // Intentionally skip the PO row + voided sub-rows. Close the
        // .rqd-fb__bucket wrapper and return early so the panel is just
        // freight cost input + (banner if pending).
        html += '</div>'; // close .rqd-fb__bucket
        return html;
      }
      /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - ONE PO ROW PER SUPPLIER IN THIS BUCKET. A line
         sent to the brand's alternate source (Recycled Surfacing for ECORE) is PO'd to that supplier,
         so a bucket holding both gets two Purchase order rows: the brand's, then each alternate with a
         line in this bucket. A PO belongs to an alternate when its vendor names it; everything else
         is the brand's (the brand's PO may read "ECORE Athletic" where the card says "ECORE"). */
      var _altNames = (q.altSourceSummary && q.altSourceSummary.rows) ? Object.keys(q.altSourceSummary.rows) : [];
      var _foldV = function (s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); };
      var _bucketLineIds = Array.isArray(bucket.lineItemIds) ? bucket.lineItemIds : null;
      var _altInBucket = _altNames.filter(function (n) {
        return (Array.isArray(q.onlineLineItems) ? q.onlineLineItems : []).some(function (it) {
          return it && _foldV(it.vendor) === _foldV(n) && (!_bucketLineIds || _bucketLineIds.indexOf(it.lineItemId) !== -1);
        });
      });
      var _isAltPo = function (p, n) { return _foldV(p.vendor) === _foldV(n) || _foldV(p.poVendor) === _foldV(n); };
      var _poTargets = _sp
        ? [{ vendor: _sp.vendor, alt: _spAlt }] // CUSTOM_ALTSHIP_BY_CLAUDE - each supplier's block holds its own PO row
        : [{ vendor: vendor, alt: false }].concat(_altInBucket
          .filter(function (n) { return _foldV(n) !== _foldV(vendor); })
          .map(function (n) { return { vendor: n, alt: true }; }));
      _poTargets.forEach(function (_pt) {
      var vendorEsc = esc(_pt.vendor || '');
      var _poLabel = _poTargets.length > 1 ? 'Purchase order &middot; ' + esc(_pt.vendor) : 'Purchase order';
      var bucketPos = (q.pos || []).filter(function (p) {
        if (!p || p.bucket !== bId) return false;
        if (_pt.alt) return _isAltPo(p, _pt.vendor);
        return !_altNames.some(function (n) { return _isAltPo(p, n); });
      });
      var activePos = bucketPos.filter(function (p) { return p.status !== 'voided'; });
      var voidedPos = bucketPos.filter(function (p) { return p.status === 'voided'; });
      var latestPo = activePos.length ? activePos[activePos.length - 1] : null;

      if (!latestPo) {
        // Empty state — Generate PO button, gated by cost (parcel) or not (broker).
        html += '<div class="rqd-toggle' + (poGated ? ' rqd-toggle--locked' : '') + '"'
          + stepAttrs('purchase-order', poGated ? 'blocked' : 'not-generated')
          + (poGated ? ' title="' + esc(poGateReason) + '" data-blocked-reason="' + esc(poGateReason) + '"' : '') + '>';
        html += '<div class="rqd-toggle__check" style="cursor:default;">' + (poGated ? '&#128274;' : '') + '</div>';
        html += '<span>' + _poLabel + '</span>';
        html += '<div class="rqd-invoice">';
        var poBtnClass = poGated ? 'rqd-invoice__link rqd-invoice__link--disabled' : 'rqd-invoice__link';
        var poBtnAttrs = poGated ? ('disabled title="' + esc(poGateReason) + '"') : 'type="button" style="cursor:pointer;"';
        html += '<button class="' + poBtnClass + '" data-po-action="generate" data-ref="' + esc(ref) + '" data-vendor="' + vendorEsc + '" data-bucket="' + esc(bId) + '" ' + poBtnAttrs + '>Generate PO</button>';
        html += '</div>';
        html += '</div>';
      } else {
        // Active PO exists — full status + actions row.
        // "Done" = sent OR downloaded (rep finished with the PO either way).
        var poSent = latestPo.status === 'sent';
        /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-13) — SCHEDULED: composed with "Send within
           business hours" outside the window, so the email is queued for the cron. Mirrors the
           Purchasing card's row exactly (dashboard-po-lens.js) — the two PO surfaces must not
           disagree about whether a PO has gone out. */
        var poScheduled = latestPo.status === 'scheduled';
        var poSchedLabel = '';
        if (poScheduled && latestPo.scheduledFor) {
          try { poSchedLabel = new Date(latestPo.scheduledFor).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }); } catch (_) {}
        }
        /* CUSTOM_POSEND_BY_CLAUDE (AIOPS suggestion ea3ab4a798eb, Ryan 2026-09-02) — ⛔ THE SECOND
           COPY OF THIS ROW, UPDATED IN THE SAME BREATH AS THE FIRST. The comment four lines up says
           these two surfaces "must not disagree about whether a PO has gone out", and `scheduled`
           has just stopped meaning one thing: it is now also "an agent queued this and the undo
           window is running" or "an agent queued this and nobody has approved it". Teaching only
           the Purchasing card would leave the Fulfillment card saying a bot-queued PO was simply
           scheduled — and, on an approval hold, printing "sends at ." with no time in it, because
           an approval has no clock. */
        var poHeldBy = poScheduled ? (latestPo.heldForAgent || '') : '';
        var poAwaitingApproval = poScheduled && (latestPo.holdMode === 'approval' || !latestPo.scheduledFor);
        /* ⚠️ `done` also counts a DOWNLOADED pdf, so a scheduled PO whose PDF someone opened would
           otherwise render the green ✓ and the done row styling — reading as "sent" on the very row
           that exists to say it has not been. Scheduled outranks it: the vendor does not have it. */
        var poDone = (poSent || !!latestPo.downloadedAt) && !poScheduled;
        /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-14) — the row still takes .rqd-toggle--done while
           scheduled, because that class is what paints the check green (.rqd-toggle--done
           .rqd-toggle__check, ~L6532) and Ryan wants the hourglass sitting in a filled green box:
           "it should fill with green when it includes the hourglass".
        
           That is also the house pattern already in this file — the freight "requested, waiting" row
           uses --done WITH an hourglass. So on this dashboard --done means "this step is handled",
           and the ICON says whether it is finished or still in flight.
        
           `done` stays FALSE while scheduled, deliberately, because it also drives the button styling:
           the Email/Re-send button must not take the sent-green ghost on a PO that has not gone out. */
        var poRowDone = poDone || poScheduled;
        var poDateVal = latestPo.sentAt || latestPo.lastSentAt || latestPo.downloadedAt || latestPo.createdAt || latestPo.generatedAt;
        var poSafe = esc(latestPo.poNumber);
        html += '<div class="rqd-toggle' + (poRowDone ? ' rqd-toggle--done' : '') + '"'
          /* The same split the Purchasing card makes: 'sent' meant BOTH a real email and a
             Mark-as-Sent that mailed nothing, and this row rendered identically for each.
             ALSO note poRowDone is true while merely SCHEDULED, which is why the state has to
             be read here rather than inferred from rqd-toggle--done. */
          /* ⛔ KEYED ON poSent, NOT poDone. `poDone` is `(poSent || downloadedAt) && !poScheduled`
             — it counts a PO whose PDF somebody merely OPENED. Reading the sent state off it would
             have this row announce `sent-emailed` for a PO that was downloaded and never sent,
             which is the same class of lie the sent/recorded split exists to end. Scheduled
             outranks downloaded for the same reason: the vendor does not have it yet. */
          /* CUSTOM_POSEND_BY_CLAUDE (Ryan 2026-09-02) — the same three-way split the Purchasing
             card makes, IN `data-state` itself for the same reason the sent/recorded split is. */
          + stepAttrs('purchase-order',
              poScheduled ? (poHeldBy ? (poAwaitingApproval ? 'scheduled-approval' : 'scheduled-agent') : 'scheduled')
                : (poSent ? (latestPo.sentExternally ? 'sent-recorded' : 'sent-emailed')
                  : (latestPo.downloadedAt ? 'downloaded' : 'draft')))
          /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — the instant this PO reached the
             supplier, on the row, so `markPoResendNeeded` can compare it against a settlement
             without looking the deal up again. A PO that has NOT gone out carries nothing: there is
             no amended-PO question to ask about one the vendor has never seen. */
          + ((poSent && (latestPo.lastSentAt || latestPo.sentAt))
              ? ' data-po-sent-at="' + esc(latestPo.lastSentAt || latestPo.sentAt) + '"' : '') + '>';
        html += '<div class="rqd-toggle__check" style="cursor:default;">' + (poDone ? '&#10003;' : (poScheduled ? '<span style="font-size:11px;line-height:1;">&#9203;</span>' : '')) + '</div>';
        html += '<span>' + _poLabel + '</span>';
        html += '<span class="rqd-toggle__date">' + poSafe + '</span>';
        /* CUSTOM_POSEND_BY_CLAUDE (Ryan 2026-09-02) — who queued it, when a bot did, and whether
           it is still stoppable. Silent on every human-scheduled PO. */
        if (poHeldBy) {
          html += '<span class="rqd-toggle__date" title="'
            + esc((poAwaitingApproval
                ? poHeldBy + ' composed this PO and it is waiting for an admin to approve it. Nothing has gone to the supplier.'
                : poHeldBy + ' queued this PO' + (poSchedLabel ? '; it sends ' + poSchedLabel : '') + ' unless it is discarded first.')
              + ' Release or discard it on the AI Agents → Approvals page.').replace(/"/g, '&quot;')
            + '">&#129302; ' + esc(poHeldBy) + (poAwaitingApproval ? ' &mdash; needs approval' : ' &mdash; undo window') + '</span>';
        }
        if (poDateVal) html += '<span class="rqd-toggle__date">' + formatDate(poDateVal) + '</span>';
        // Slim italic "Void" link — destructive, kept quiet alongside the
        // routine Email button. Matches the Purchasing card's pattern.
        html += '<button class="rqd-freight-reset-link" data-po-action="void" data-po="' + poSafe + '" type="button" title="Void this PO — locked audit-trail row remains below.">Void</button>';
        html += '<div class="rqd-invoice">';
        // View PO PDF (sent POs only — before send, admin previews via the modal).
        if (poSent) {
          html += '<button class="rqd-invoice__link rqd-invoice__generate" data-po-action="view-pdf" data-po="' + poSafe + '" type="button" title="Open the sent PO PDF in a new tab">View PO</button>';
        }
        // Email / Re-send PO — done-state gets the green ghost-outline variant.
        if (poScheduled) {
          /* CUSTOM_RELEASENOW_BY_CLAUDE (Ryan 2026-08-29) — same release pill as the Purchasing
             card's PO row; the two surfaces render the same PO and must offer the same thing. */
          /* CUSTOM_POSEND_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE FALLBACK PILL PRINTED A TIME THAT
             DOES NOT EXIST on an approval hold (`scheduledFor` is null by design, so this read
             "Abby sends it at ."), and pointed at Void + Re-gen, which is not the control for a
             held one. Mirrors the Purchasing card's wording exactly — two surfaces, one sentence. */
          html += (scheduledReleaseBtnHtml({ kind: 'po', ref: ref, poNumber: latestPo.poNumber, scheduledFor: latestPo.scheduledFor })
            || '<button class="rqd-invoice__link rqd-po-scheduled" type="button" disabled title="'
               + esc(poAwaitingApproval
                   ? (poHeldBy || 'An agent') + ' composed this PO and it is waiting for an admin to approve it. Nothing has gone to the supplier. Release or discard it on the AI Agents → Approvals page.'
                   : poHeldBy
                     ? (poHeldBy + ' queued this PO' + (poSchedLabel ? '; it sends ' + poSchedLabel : '')
                        + ' unless it is discarded first. Release or discard it on the AI Agents → Approvals page.')
                     : ('Queued - Abby sends it at ' + poSchedLabel + '. Void + Re-gen to change it.')).replace(/"/g, '&quot;')
               + '">&#9203; ' + (poAwaitingApproval ? 'Awaiting approval' : 'Scheduled') + '</button>');
        } else {
          html += '<button class="rqd-invoice__link' + (poDone ? ' rqd-invoice__generate' : '') + '" data-po-action="email" data-po="' + poSafe + '" data-vendor="' + vendorEsc + '" data-ref="' + esc(ref) + '" type="button">' + (poSent ? 'Re-send PO' : 'Email PO') + '</button>';
        }
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-12) — record a PO that reached the vendor
           another way (plain email, phone, portal upload) so the deal can close. Sends nothing.
           Only on POs not already sent — once sent there is nothing to record, and the row
           already offers Re-send. Sits right after Email PO because that is where you are
           standing when you realise the send was never captured (SHOP-1743). */
        if (!poSent && !poScheduled) {
          html += '<button class="rqd-invoice__link" data-po-action="mark-sent" data-po="' + poSafe + '" data-vendor="' + vendorEsc + '" data-ref="' + esc(ref) + '" type="button" title="Record this PO as already sent to the vendor — no email is sent">Mark Sent</button>';
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        // Re-gen mints a new revision (-B / -C / ...) when prior was sent.
        html += '<button class="rqd-invoice__link rqd-invoice__generate" data-po-action="regen" data-ref="' + esc(ref) + '" data-vendor="' + vendorEsc + '" data-bucket="' + esc(bId) + '" type="button" title="Mint a new revision — the current PO stays SENT in the audit trail - nothing voids it automatically.">Re-gen</button>';
        html += '</div>';
        html += '</div>';
      }
      // Voided audit-trail sub-rows — locked + muted for record-keeping.
      voidedPos.forEach(function (vp) {
        html += '<div class="rqd-toggle rqd-toggle--locked rqd-po-voided-row">';
        html += '<div class="rqd-toggle__check" style="cursor:default;">&#128274;</div>';
        html += '<span>' + esc(vp.poNumber) + ' voided</span>';
        if (vp.voidedAt) html += '<span class="rqd-toggle__date">' + formatDate(vp.voidedAt) + '</span>';
        html += '</div>';
      });
      }); // CUSTOM_ALTSOURCE_BY_CLAUDE - end of the per-supplier PO rows

      html += '</div>'; // close .rqd-fb__bucket
      return html;
    }

    // === renderFreightBalanceCard — extracted from main.js:6938-7214 ===
    function renderFreightBalanceCard(q) {
      // isAdmin mutates after auth resolves (main.js:301) so resolve via getter
      // every call rather than the value at init time.
      var isAdmin = getIsAdmin ? getIsAdmin() : false;
      var ref = q.reference;
      var vendors = (q.financials || {}).vendors || [];
      // v1: single-vendor only. Multi-vendor flows (rare on Flow A) get a
      // disabled placeholder until we wire an aggregator.
      if (vendors.length === 0) return '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Card title is dynamic. When the
         vendor isn't post-ship-invoice and the card is hosting bucket
         panels because the Purchasing card has hidden itself, "Freight
         Balance" no longer describes what the card actually does — the
         dominant purpose is PO + bucket-level workflow management.
         Rename to "Fulfillment" in that case (per Ryan), covering the
         broader getting-the-order-to-the-customer process: PO gen, send
         to vendor, receive, etc. Hyperice keeps "Freight Balance" since
         post-ship freight invoice entry is still the dominant purpose. */
      var _titleVendor = vendors[0] || '';
      var _titleIsPostShip = isPostShipInvoiceVendor(_titleVendor);
      var _cardTitle = _titleIsPostShip ? 'Freight Balance' : 'Fulfillment';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_ALTSOURCE_BY_CLAUDE (Ryan 2026-10-07) - a brand's alternate source (Recycled Surfacing
         for ECORE) is not a second vendor here: the card stays the brand's and each bucket draws one
         PO row per supplier (renderShippingBucketPanel). */
      var _altRows = (q.altSourceSummary && q.altSourceSummary.rows) || {};
      var _foldAlt = function (s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); };
      var _altKeys = Object.keys(_altRows).map(_foldAlt);
      var _ownVendors = vendors.filter(function (v) { return _altKeys.indexOf(_foldAlt(v)) === -1; });
      if (_ownVendors.length >= 1 && _ownVendors.length < vendors.length) {
        vendors = _ownVendors;
        _titleVendor = vendors[0] || '';
      }
      if (vendors.length > 1) {
        return '<div class="rqd-detail__arap rqd-fb">' +
          sectionTitleHtml(_cardTitle, isFreightBalanced(q)) +
          '<div class="rqd-fb__multi-notice">Multi-vendor freight balance not yet supported. Generate freight invoice manually for now (one consolidated draft order in Shopify Admin).</div>' +
          '</div>';
      }
      var vendor = vendors[0];
      var fq = (q.freightQuotes || {})[vendor] || {};
      var fb = q.freightBalance || {};
      var bc = q.balanceCollected || {};

      var requested = !!fq.requestedAt;
      // Dims may be a string (legacy) or { raw, summary, confidence, ... }
      // (new structured shape). Reading code normalizes both.
      var dimsObj = (fb.dims && typeof fb.dims === 'object') ? fb.dims : null;
      var dimsRaw = dimsObj ? (dimsObj.raw || '') : (fb.dims || '');
      var dimsSummary = (dimsObj && dimsObj.summary) || '';
      var dimsConfidence = (dimsObj && dimsObj.confidence) || 'failed';
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — N/A skip (supplier shipped).
      var dimsNa = !!(dimsObj && dimsObj.na);
      var dimsEntered = !!(dimsRaw && String(dimsRaw).trim().length > 0);
      var invoiceGenerated = !!fb.draftOrderId;
      var emailed = !!fb.emailedAt;
      var paid = !!bc.paid;
      // A linked balance child order (admin used Link Balance Order to attach
      // a separate Shopify order like SHOP-1683 to the parent SHOP-1654) IS
      // the balance payment — auto-satisfies the "balance paid" step + the
      // PO gate without requiring the Flow A invoice flow to complete.
      var hasLinkedChild = Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0;
      var paidEffective = paid || hasLinkedChild;

      var rows = '';
      /* CUSTOM_ONLINEWILLCALL_BY_CLAUDE (ONYX 39db36f688e9 + 96dc64a31604, Ryan 2026-10-26, SHOP-1923) - the customer
         collects the WHOLE online order. Off: a dashed "Will-call (customer pickup)" button. On (q.willCall, from the
         deal's will-call record): the banner with the pickup note, Undo, and "Refund freight" while the shipping the
         customer paid is not refunded, which opens the Adjust Order card with the freight already set to $0. */
      if (isAdmin && /^SHOP-\d+$/.test(String(ref || ''))) {
        var _wc = q.willCall || null;
        if (_wc) {
          var _fin = q.financials || {};
          var _shipLeft = Math.max(0, (parseFloat(_fin.freightCharged) || 0) - (parseFloat(_fin.freightRefunded) || 0));
          var _wcRefunded = _wc.freightRefund || null;
          rows += '<div class="rqd-fb__bypass rqd-fb__bypass--on rqd-fb__willcall">'
            + '<span>🏬 Will-call (customer pickup)' + (_wc.fromDropdown ? ', set on the shipping drop-down' : ': ' + esc(_wc.note || '')) + (_wc.pickupAt ? ' · pickup ' + esc(_wc.pickupAt) : '') // fromDropdown: CUSTOM_DROPDOWNWILLCALL_BY_CLAUDE
            + (_wcRefunded ? ' · freight refunded $' + (Number(_wcRefunded.amount) || 0).toFixed(2) : '') + '</span>'
            + '<span>'
            + ((!_wcRefunded && _shipLeft > 0.01)
                ? '<button type="button" class="rqd-invoice__link rqd-fb__willcall-refund" data-fb-action="willcall-refund" data-ref="' + esc(ref) + '" title="Opens Adjust Order with the freight set to $0, so the shipping the customer paid comes back to them. Lower the refund by raising the Cost box. Nothing moves until you commit and release it there.">Refund freight $' + _shipLeft.toFixed(2) + '</button> '
                : '')
            /* CUSTOM_DROPDOWNWILLCALL_BY_CLAUDE - set on the drop-down: the drop-down is the undo, so no Undo button here. */
            + (_wc.fromDropdown ? '' : '<button type="button" class="rqd-fb__bypass-clear" data-fb-action="willcall-undo" data-ref="' + esc(ref) + '" title="The order ships after all: freight asks and shipping emails come back on.">Undo will-call</button>')
            + '</span></div>';
        } else if (Array.isArray(q.shippingBuckets) && q.shippingBuckets.length > 1) {
          /* CUSTOM_DROPDOWNWILLCALL_BY_CLAUDE (Ryan 2026-10-26: "hide it on single-shipment orders") - with one shipment its
             Will Call drop-down IS the whole-order switch, so the button only shows where it saves setting several. */
          rows += '<div class="rqd-fb__bypass"><button type="button" class="rqd-fb__bypass-set" data-fb-action="willcall-set" data-ref="' + esc(ref) + '" title="The customer picks the whole order up instead of a delivery. Stops freight and dims asks, the tracking / shipped emails and the pickup-PRO chase; the customer\'s updates say ready for pickup. A truck already booked is not cancelled.">Will-call (customer pickup)</button></div>';
        }
      }

      // CUSTOM_RFQPORTAL_BY_CLAUDE — Shipping bucket panels.
      // When the order has bucket(s) (auto-detected from cart product
      // tags), each bucket renders a self-contained sub-workflow that
      // includes its OWN request row + (for broker buckets) the dims-
      // received row, so we skip the legacy top-of-card Row 1 + Row 2
      // entirely. Bucket panels take precedence over the legacy invoice
      // path: once admin has bucket data, the bucket flow is the
      // authoritative surface for PO + freight, regardless of whether
      // a stale FREIGHT-{ref} draft invoice lingers in KV from earlier
      // testing (SHOP-1681 case — caused the Purchasing card to render
      // its Generate PO button without a bucket key).
      var hasBuckets = Array.isArray(q.shippingBuckets) && q.shippingBuckets.length > 0;
      var useBucketPanels = hasBuckets;
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      if (!useBucketPanels) {
      // Row 1 — Weight/Dims Requested (legacy top-of-card placement)
      rows += '<div class="rqd-toggle' + (requested ? ' rqd-toggle--done' : '') + '" data-ref="' + esc(ref) + '">';
      rows += '<div class="rqd-toggle__check" style="cursor:default;">' + (requested ? '&#10003;' : '') + '</div>';
      rows += '<span>Weight/dims requested from ' + esc(vendor) + '</span>';
      if (requested) {
        rows += '<span class="rqd-toggle__date">' + requestedStampHtml(fq) + '</span>';
        rows += '<div class="rqd-invoice" style="margin-left:auto;">';
        // Step done → button gets the green .rqd-invoice__generate
        // variant. Active/incomplete-step buttons stay default so the
        // eye is drawn to whatever needs attention next.
        rows += '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="request-dims" data-ref="' + esc(ref) + '" data-vendor="' + esc(vendor) + '" title="Re-open the modal to review & re-send the request email">Re-send</button>';
        rows += '</div>';
      } else {
        rows += '<div class="rqd-invoice" style="margin-left:auto;">';
        rows += '<button class="rqd-invoice__link rqd-fb-btn--primary" data-fb-action="request-dims" data-ref="' + esc(ref) + '" data-vendor="' + esc(vendor) + '" title="Email ' + esc(vendor) + '">Email ' + esc(shortVendorLabel(vendor)) + '</button>';
        rows += '</div>';
      }
      rows += '</div>';

      // Row 2 — Dims received + entered (gated on row 1).
      // Three render branches:
      //   1. dimsEntered → standalone toggle: ✓ + saved dims summary + Edit
      //   2. !dimsEntered && requested → renderFreightBalancePalletForm()
      //      which produces an inline toggle: "Weight/dims received" label
      //      + Qty × L × W × H + Weight inputs ON THE SAME LINE, with the
      //      +Add / Save actions row below. Single-line entry by default;
      //      multi-pallet additions stack inside the toggle's inputs slot.
      //   3. !dimsEntered && !requested → locked toggle (🔒, waiting on
      //      row 1 to fire the dims request).
      if (dimsNa) {
        rows += renderDimsNaDoneRow(ref, dimsObj);
      } else if (dimsEntered) {
        rows += '<div class="rqd-toggle rqd-toggle--done" data-ref="' + esc(ref) + '">';
        rows += '<div class="rqd-toggle__check" style="cursor:default;">&#10003;</div>';
        rows += '<span>Weight/dims received</span>';
        var displayDims = dimsSummary || dimsRaw;
        // Use the standard .rqd-toggle__date treatment (italic muted text
        // with the " – " prefix) so all inline meta on this card has a
        // consistent visual rhythm with row 1's date.
        rows += '<span class="rqd-toggle__date">' + esc(displayDims) + '</span>';
        rows += '<div class="rqd-invoice" style="margin-left:auto;">';
        rows += '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="edit-dims" data-ref="' + esc(ref) + '">Edit</button>';
        rows += '</div>';
        rows += '</div>';
      } else if (requested) {
        // Inline pallet form — toggle row + inputs + actions all wrapped
        // by .rqd-fb-pallet-form so the add/save handlers still find their
        // container via .closest().
        rows += renderFreightBalancePalletForm(ref, []);
      } else {
        rows += '<div class="rqd-toggle rqd-toggle--locked" data-ref="' + esc(ref) + '" title="Request dims from the supplier first.">';
        rows += '<div class="rqd-toggle__check" style="cursor:default;">&#128274;</div>';
        rows += '<span>Weight/dims received</span>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-16) — N/A reachable even when
           dims were never requested (supplier shipped on their own carrier). */
        rows += '<div class="rqd-invoice" style="margin-left:auto;">';
        rows += '<button class="rqd-invoice__link rqd-fb-pallet-na" data-fb-action="dims-na" data-ref="' + esc(ref) + '" title="No weight/dims will be received (supplier shipped on their own carrier). Marks this step complete so the order can close out. Reversible.">N/A</button>';
        rows += '</div>';
        rows += '</div>';
      }
      } // end !useBucketPanels — legacy Row 1 + Row 2 placement

      if (useBucketPanels) {
        /* CUSTOM_VENDORHEAD_BY_CLAUDE (Ryan 2026-08-28) — name the supplier above the panels.
           Ryan, after tagging TRD-000002 freight-quoted on SHOP-1996: *"in OFFLINE deals there is a
           sub-header at the top that names the vendor ... On this ONLINE deal however that goes away
           when the 'Freight-quoted' tag gets added, and it gets replaced with 'Broker freight 1
           item'. I still think we need the brand name sub-header above all this."*

           He is right, and the disappearance is a side effect rather than a decision: adding the tag
           flips the deal to Flow A, which suppresses the Purchasing card (where the vendor header
           lives) and moves the workflow into the bucket panels here — which had no header of their
           own. The bucket LABEL is not a substitute: "Broker freight · 1 item" says how the goods
           travel, never who is shipping them.

           ⚠️ Reuses `.rqd-po-group`, the Purchasing card's own vendor header class, rather than
           minting a second one — so the two cards state the supplier in exactly the same voice and
           cannot drift apart later. */
        /* CUSTOM_ALTSHIP_BY_CLAUDE - a shared bucket names each supplier on its own block, so the one header
           above would only repeat the first of them. */
        var _anyShared = q.shippingBuckets.some(function (b) { return b && Array.isArray(b.suppliers) && b.suppliers.length; });
        if (vendor && !_anyShared) rows += '<div class="rqd-po-group">' + esc(vendor) + '</div>';
        // Each bucket panel includes its own request row + (for broker
        // buckets) the dims-received row, so the entire Row 1 / Row 2
        // workflow moves inside the matching bucket.
        for (var bi = 0; bi < q.shippingBuckets.length; bi++) {
          var _bk = q.shippingBuckets[bi];
          if (_bk && Array.isArray(_bk.suppliers) && _bk.suppliers.length) {
            rows += renderSharedBucket(_bk, q, ref, dimsEntered, dimsRaw, dimsSummary, vendor);
            continue;
          }
          rows += renderShippingBucketPanel(_bk, q, ref, dimsEntered, dimsRaw, dimsSummary, vendor);
        }
      }

      // ── Legacy Row 3 / Row 4 (Flow A invoice path) ──
      // Preserved for orders that previously generated a FREIGHT-{ref}
      // draft invoice. Three display states:
      //   1. invoiceGenerated → green ✓, cost/charge summary + Edit
      //   2. hasLinkedChild (no invoice yet) → "n/a" in the check slot,
      //      inputs collapsed behind an "Edit" button.
      //   3. dimsEntered → normal expanded inputs + "Invoice + Email"
      //   4. else → 🔒 (waiting on row 2)
      if (!useBucketPanels) {
      var row3Linked = hasLinkedChild && !invoiceGenerated;
      // n/a appearance matches the AR/AP Invoiced row when it falls back to
      // n/a (residential / online orders): row gets .rqd-toggle--done so it
      // visually reads as "settled / doesn't apply", and the check div gets
      // .rqd-toggle__check--na with the literal "n/a" text.
      var row3RowDone = invoiceGenerated || row3Linked;
      var row3CheckCls = row3Linked ? ' rqd-toggle__check--na' : '';
      var row3CheckGlyph = invoiceGenerated ? '&#10003;'
        : row3Linked ? 'n/a'
        : !dimsEntered ? '&#128274;'
        : '';
      rows += '<div class="rqd-toggle' + (row3RowDone ? ' rqd-toggle--done' : '') + ((!dimsEntered && !row3Linked) ? ' rqd-toggle--locked' : '') + '" data-ref="' + esc(ref) + '"' + ((!dimsEntered && !row3Linked) ? ' title="Enter received dims first."' : (row3Linked ? ' title="Balance paid via linked order — in-app invoice flow not needed."' : '')) + '>';
      rows += '<div class="rqd-toggle__check' + row3CheckCls + '" style="cursor:default;">' + row3CheckGlyph + '</div>';
      rows += '<span>Broker freight quote</span>';
      if (invoiceGenerated) {
        var brokerCostStr = money(fb.brokerCost || 0);
        var customerChargeStr = money(fb.customerCharge != null ? fb.customerCharge : (fb.brokerCost || 0));
        var costDisplay = (fb.customerCharge != null && fb.customerCharge !== fb.brokerCost)
          ? (brokerCostStr + ' cost / ' + customerChargeStr + ' charged')
          : customerChargeStr;
        rows += '<span class="rqd-toggle__date">' + costDisplay + '</span>';
        rows += '<div class="rqd-invoice" style="margin-left:auto;">';
        if (fb.invoiceUrl) {
          rows += '<a class="rqd-invoice__link rqd-invoice__generate" href="' + esc(fb.invoiceUrl) + '" target="_blank" title="Opens the freight balance draft order' + (fb.draftOrderName ? ' (' + esc(fb.draftOrderName) + ')' : '') + '">&#128279; BALANCE</a>';
        }
        rows += '<button class="rqd-invoice__link rqd-invoice__generate" data-fb-action="edit-cost" data-ref="' + esc(ref) + '">Edit</button>';
        rows += '</div>';
      } else if (row3Linked || dimsEntered) {
        // Wrap inputs + Invoice+Email button in a collapsible cluster.
        // When hasLinkedChild (no invoice yet) → starts collapsed with
        // just an "Edit" button visible; clicking Edit expands.
        // When normal Flow A (no linked child) → starts expanded.
        var collapsed = row3Linked;
        rows += '<div class="rqd-invoice rqd-fb-row3' + (collapsed ? ' rqd-fb-row3--collapsed' : '') + '" style="margin-left:auto;">';
        // Collapsed-state Edit button — uses the same green
        // .rqd-invoice__generate variant as the Edit button on Row 2
        // (Weight/dims received), so completed/skipped rows have a
        // consistent affordance for retroactive edits.
        rows += '<button class="rqd-invoice__link rqd-invoice__generate rqd-fb-row3__edit" data-fb-action="expand-broker-quote" data-ref="' + esc(ref) + '" title="Reveal the freight charge input and the Invoice + Email button. The linked balance order means an invoice isn\'t typically needed, but you can still record the freight charge here for tracking.">Edit</button>';
        // CUSTOM_ABBY_BY_CLAUDE (2026-06-02) — Pass-through pricing: the rep
        // enters the customer-facing freight charge (markup already baked in),
        // so the markup% + separate "charge" inputs were removed. The kept
        // input retains the .rqd-fb-cost-input class the handlers read.
        rows += '<div class="rqd-fb-row3__form">';
        rows += '<div class="rqd-fb-cost-input-inner" title="Freight charge — the amount the customer pays (enter it with your markup already included)">';
        rows += '<span class="rqd-fb-cost-prefix">$</span>';
        rows += '<input type="number" step="0.01" min="0" class="rqd-fb-cost-input" data-ref="' + esc(ref) + '" placeholder="freight charge">';
        rows += '</div>';
        rows += '<button class="rqd-invoice__link rqd-fb-btn--primary" data-fb-action="generate-invoice" data-ref="' + esc(ref) + '">Invoice + Email</button>';
        rows += '</div>';
        rows += '</div>';
      }
      rows += '</div>';

      // Row 4 — Balance paid (gated on row 3, but auto-satisfied when a
      // linked balance child order is present — that order IS the payment).
      // When the linked-child path is used, the row 3 gate is irrelevant —
      // the balance was settled via a separate Shopify order, not via the
      // FREIGHT-{ref} synthetic draft. So we DON'T render the lock state
      // when hasLinkedChild is true even if invoiceGenerated is false.
      var row4Locked = !invoiceGenerated && !hasLinkedChild;
      rows += '<div class="rqd-toggle' + (paidEffective ? ' rqd-toggle--done' : '') + (row4Locked ? ' rqd-toggle--locked' : '') + '" data-ref="' + esc(ref) + '"' + (row4Locked ? ' title="Generate the invoice first."' : '') + '>';
      rows += '<div class="rqd-toggle__check" style="cursor:default;">' + (paidEffective ? '&#10003;' : (row4Locked ? '&#128274;' : '')) + '</div>';
      rows += '<span>Balance paid by customer</span>';
      if (paidEffective) {
        if (hasLinkedChild && !paid) {
          // Auto-paid via linked child — show the child ref(s) so admin
          // sees which separate order represents the payment.
          var childRefsRow4 = q.balanceChildren.map(function (c) { return esc(c.reference || c.orderName || ''); }).filter(Boolean).join(', ');
          rows += '<span class="rqd-toggle__date">via ' + childRefsRow4 + '</span>';
          rows += '<span class="rqd-fb-paid-source" title="Detection source">🔗 linked</span>';
        } else {
          rows += '<span class="rqd-toggle__date">' + formatDate(bc.paidAt) + '</span>';
          if (bc.source) rows += '<span class="rqd-fb-paid-source" title="Detection source">' + (bc.source === 'webhook' ? '⚡ auto' : '✋ manual') + '</span>';
        }
      } else if (invoiceGenerated) {
        if (emailed) {
          rows += '<span class="rqd-toggle__date">Emailed ' + formatDate(fb.emailedAt) + ' &middot; awaiting payment</span>';
        } else {
          rows += '<span class="rqd-toggle__date">Invoice generated &middot; email not yet sent</span>';
        }
        rows += '<div class="rqd-invoice">';
        // When the invoice was generated but the email never sent (admin
        // cancelled the modal), surface a "Send Email" button that re-
        // opens the preview modal using the EXISTING draft order — no
        // new Shopify draft minted, no duplicate. Both buttons use
        // .rqd-invoice__link for the standard 140×28 primary pill so
        // they line up with Re-send / Generate PO across the panel.
        if (!emailed) {
          rows += '<button class="rqd-invoice__link" data-fb-action="send-email" data-ref="' + esc(ref) + '" title="Re-open the email preview modal using the existing FREIGHT-{ref} draft order — no duplicate is created.">Send Email</button>';
        }
        rows += '<button class="rqd-invoice__link" data-fb-action="mark-paid" data-ref="' + esc(ref) + '" title="Use only if the orders/paid webhook hasn&#39;t auto-flipped this — typically Shopify pings within seconds of customer payment.">Mark Paid</button>';
        rows += '</div>';
      }
      rows += '</div>';
      } // end if (!useBucketPanels) — legacy Row 3 + Row 4 block

      // (Customer balance row moved into renderShippingBucketPanel — it now sits
      // between Freight cost and the PO row, per the logical flow. 2026-06-02.)

      // Bypass banner / control — Flow A balance can be "handled" two ways
      // that warrant a banner here:
      //   1. Explicit admin override: fb.handledExternally flag stamped via
      //      /admin/freight-balance-bypass. Renders the banner so admin can
      //      see + clear the override.
      //   2. Normal Flow A: Invoice + Email + customer pays (existing flow).
      // The auto-detected "linked balance child" case used to render a third
      // banner here ("Balance paid via linked order: SHOP-XXXX") but that
      // info now lives on the Linked Balance Reconciliation card above
      // Order Contents — banner removed as redundant.
      /* CUSTOM_BALANCEBTN_BY_CLAUDE (Ryan 2026-08-28) — ⛔ THE TOP-OF-CARD BYPASS BANNER IS GONE,
         both halves of it. The SET button moved onto the Customer balance row earlier today; the
         "✓ Balance handled externally · date · by RW / Clear override" banner that replaced it once
         set is retired here. Ryan: *"This banner doesn't need to exist."*

         It reported a single row's state across the full width of the card, and reported it twice —
         the row itself already renders "handled externally · date · who". Its Clear override button
         is now the ghosted **Balanced** button in the row, where the thing it undoes lives. */
      /* CUSTOM_BALANCEBTN_BY_CLAUDE (Ryan 2026-08-28) — ⛔ THE WIDE "Mark balance handled
         externally" BUTTON THAT SAT HERE HAS MOVED onto the Customer balance row, beside
         Invoice + Email. Ryan: *"It's in the wrong place. It should look just like the
         'Invoice + Email' button, on that same row, next to it."* It is one of the two ways to
         settle that balance, so it belongs with the other one rather than reading as a header
         control above the whole card.

         The "on" banner above STAYS here: once the override is set it is a statement about the
         whole card (the PO gate is unlocked), not a row-level action, and Clear override has to
         stay reachable from the top.

         ⚠️ THIS NARROWS WHERE THE BUTTON EXISTS, which a pure move would not. The Customer
         balance row renders only for a BROKER bucket on a non-post-ship vendor, so the bypass is
         no longer offered on parcel/combined buckets or on Hyperice. That is the only state where
         the Flow A balance gate it unlocks actually applies — but it is a real reduction in reach,
         not a relocation, and it is written down here rather than discovered later. */

      return '<div class="rqd-detail__arap rqd-fb" data-ref="' + esc(ref) + '">' +
        sectionTitleHtml(_cardTitle, isFreightBalanced(q)) +
        '<div class="rqd-detail__arap-row">' + rows + '</div>' +
        '</div>';
    }

  // === Init contract ===
  /* ============================================
     CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — "RE-SEND AMENDED PO"
     ============================================
     Ryan on SHOP-1941: the customer paid, we collected a balance and placed the PO with ECORE, he
     then asked to add more, we raised an Adjust Order collect without cancelling the PO — *"Then he
     did pay for it, so now it's time to send our vendor an amended PO."* He asked for *"a red
     checkbox with a white exclamation mark, with a small/concise note saying 'Re-send amended PO'…
     Once it's set, everything can go back to being green again."*

     ⛔ THE SIGNAL IS MONEY SETTLED AFTER THE PO WENT OUT, not line drift. `lib/po-order-drift.js`
     already answers the drift question and is the better tool where it applies — but it reports
     nothing for a PO with no `orderSnapshot`, and SHOP-1941-ECORE has none (it was minted the same
     day that shipped). This asks a question needing no snapshot: did the customer pay for MORE
     after we ordered? An adjustment that settled later is exactly that, and it is the case Ryan
     hit.

     ⚠️ SETTLED ONLY. A pending collect is a question, not a change: Ryan raised this one *"not
     knowing if he'd actually pay for it"*, and flagging the PO then would have cried wolf on a
     balance that might never land. `paid` for a collect, `released` for a refund — the two stamps
     handlers/order-adjust-routes.js writes when the state actually flips.

     ⚠️ AND IT CLEARS ITSELF. The comparison is against the PO's own `lastSentAt || sentAt`, so
     re-sending after the settlement puts the row back to green with nothing to reset by hand —
     which is what Ryan asked for and is also the only version that cannot go stale in the other
     direction.

     📌 DOM-ONLY, called by the card that already has the data. The adjustments are fetched lazily
     by dashboard-adjust-order.js for its own history; asking for them a second time here would be
     a second round trip for one panel. It hands them over instead. */
  function markPoResendNeeded(root, adjustments) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var rows = scope.querySelectorAll('.rqd-toggle[data-step="purchase-order"][data-po-sent-at]');
    if (!rows || !rows.length) return 0;
    /* The latest instant any adjustment actually settled. */
    var latest = 0;
    (Array.isArray(adjustments) ? adjustments : []).forEach(function (a) {
      var st = (a && a.settlement) || {};
      if (st.status !== 'paid' && st.status !== 'released') return;
      var t = Date.parse(st.paidAt || st.releasedAt || '');
      if (t && t > latest) latest = t;
    });
    var marked = 0;
    Array.prototype.forEach.call(rows, function (row) {
      var sent = Date.parse(row.getAttribute('data-po-sent-at') || '');
      var stale = !!latest && !!sent && latest > sent;
      row.classList.toggle('rqd-toggle--po-resend', stale);
      var check = row.querySelector('.rqd-toggle__check');
      if (check) check.innerHTML = stale ? '!' : '&#10003;';
      var note = row.querySelector('.rqd-po-resend-note');
      if (stale && !note) {
        note = document.createElement('span');
        note.className = 'rqd-po-resend-note';
        note.textContent = 'Re-send amended PO';
        note.title = 'The customer paid for a change AFTER this PO went to the supplier. '
          + 'Re-send it so they ship what was actually bought.';
        var label = row.querySelector('span');
        if (label && label.nextSibling) row.insertBefore(note, label.nextSibling);
        else row.appendChild(note);
      } else if (!stale && note && note.parentNode) {
        note.parentNode.removeChild(note);
      }
      /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE BUTTON THAT FIXES IT MUST NOT
         LOOK DONE. Re-gen ships wearing `.rqd-invoice__generate`, the GHOSTED done-state — right on
         a PO nobody is waiting on, wrong the moment the row beside it is asking for an amended one.
         Ryan: *"can you make the Re-gen button back to its original green color config as opposed
         to ghosted/grayed out?"*

         📌 `--active` is the EXISTING "needs to fire" variant of that same class — already declared
         in the base, dark AND sold scopes, and in the sold block it carries `!important` at the same
         (0,2,0) as the ghost precisely so that coming later in the file is what wins. Joining it is
         the whole change; a third green would repeat the mistake PM42 made.

         ⚠️ Re-gen, not Re-send: the amended document does not exist yet. Re-send would put the
         ALREADY-SENT PDF back in ECORE's inbox, which is the one outcome this row exists to
         prevent. */
      var regen = row.querySelector('[data-po-action="regen"]');
      if (regen && regen.classList) regen.classList.toggle('rqd-invoice__generate--active', stale);
      if (stale) marked++;
    });
    return marked;
  }
  window.RQD.markPoResendNeeded = markPoResendNeeded;

  window.RQD.initShippingCards = function(deps) {
    esc                     = deps.esc;
    money                   = deps.money;
    formatDate              = deps.formatDate;
    sectionTitleHtml        = deps.sectionTitleHtml;
    isFreightBalanced       = deps.isFreightBalanced;
    isPostShipInvoiceVendor = deps.isPostShipInvoiceVendor;
    getIsAdmin              = deps.getIsAdmin;
  };

  // === Public API ===
  window.RQD.renderShippingBucketPanel      = renderShippingBucketPanel;
  window.RQD.renderFreightBalanceCard       = renderFreightBalanceCard;
  window.RQD.renderFreightBalancePalletRow  = renderFreightBalancePalletRow;
  window.RQD.renderFreightBalancePalletForm = renderFreightBalancePalletForm;
})();
